import React, { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { et } from 'date-fns/locale/et';
import { enUS as en } from 'date-fns/locale/en-US';
import activities from './activities/activities.json';

import { Country } from 'world_countries_lists';
import 'react-datepicker/dist/react-datepicker.css';

// Environment configuration
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3000/auth/sign-with-apikey';
const KYC_API_BASE_URL = import.meta.env.VITE_KYC_API_BASE_URL || 'http://localhost:3000/kyc';
const REDIRECT_URL = import.meta.env.VITE_REDIRECT_URL || 'https://varahaldus.greenit.ee/';

// Register Estonian locale for date picker
registerLocale('et', et as any);

// Custom styles for date picker to match our design
const datePickerStyles = {
  input: {
    backgroundColor: '#F7F7FA',
    border: '1px solid transparent',
    borderRadius: '8px',
    padding: '8px 12px',
    width: '100%',
    height: '48px',
    outline: 'none',
    fontSize: '14px'
  }
};

const steps = [
  { key: 'step1' },
  { key: 'step2' },
  { key: 'step3' },
  { key: 'step4' },
  { key: 'step5' },
  { key: 'step6' },
];

const activityOptions = activities.map((activity, index) => ({
  value: `activity_${index}`,
  label: activity
}));

// Custom styles for react-select to match our design
const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: '#F7F7FA',
    border: state.isFocused ? '1px solid #6B897B' : '1px solid transparent',
    borderRadius: '8px',
    boxShadow: 'none',
    minHeight: '48px',
    height: '48px',
    '&:hover': {
      border: '1px solid #6B897B'
    }
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#6B897B' : state.isFocused ? '#E8EDEA' : 'white',
    color: state.isSelected ? 'white' : '#4A5568',
    padding: '12px 16px',
    '&:hover': {
      backgroundColor: state.isSelected ? '#6B897B' : '#E8EDEA'
    }
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: '#E0E3E1',
    borderRadius: '12px',
    margin: '3px 6px 3px 0',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid #E5E7EB'
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: '#515754',
    padding: '6px 0px 6px 14px',
    fontWeight: '500',
    fontSize: '13px'
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    color: '#6B7280',
    borderRadius: '0 12px 12px 0',
    padding: '6px 8px',
    '&:hover': {
      backgroundColor: '#E0E3E1',
      color: '#374151',
      borderRadius: '0 12px 12px 0'
    }
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#9CA3AF'
  }),
  singleValue: (provided: any) => ({
    ...provided,
    color: '#4A5568'
  }),
  input: (provided: any) => ({
    ...provided,
    color: '#4A5568'
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px'
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 9999
  })
};

// Import countries data based on current language
const getCountriesData = async (language: string): Promise<Country[]> => {
  try {
    // Import the appropriate language file
    if (language === 'et') {
      const data = await import('world_countries_lists/data/countries/et/countries.json');
      return data.default || data;
    } else {
      const data = await import('world_countries_lists/data/countries/en/countries.json');
      return data.default || data;
    }
  } catch (error) {
    // Fallback to English if language file doesn't exist
    const data = await import('world_countries_lists/data/countries/en/countries.json');
    return data.default || data;
  }
};

// Experience conversion functions
const convertMonthsToYearsAndMonths = (totalMonths: number): { years: number; months: number } => {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return { years, months };
};

const convertYearsAndMonthsToMonths = (years: number, months: number): number => {
  return years * 12 + months;
};

type FormState = {
  // Step 1: Company Data
  companyName: string;
  registryCode: string;
  telephone: string;
  email: string;
  // Legal Address fields
  legalAddress: string;
  legalCity: string;
  legalCounty: string;
  legalCountry: string;
  legalZipCode: string;
  // Business Address fields
  businessAddress: string;
  businessCity: string;
  businessCounty: string;
  businessCountry: string;
  businessZipCode: string;
  differentAddress: boolean;
  
  // Step 2: Business Activity
  activity: string;
  activityText: string; // Store the selected activity's text/label
  activityDescription: string;
  activityAreas: string[];
  mainPartners: string;
  vatNumber: string;
  
  // Step 3: Board Members
  boardMembers: BoardMember[];
  
  // Step 4: Beneficial Owners
  beneficialOwners: BeneficialOwner[];
  beneficialOwnersObligatory: boolean;
  reasonOfNoBO: string;
  
  // Step 5: Politically Exposed Person
  hasPoliticalMembers: boolean;
  politicalMembers: PoliticalMember[];
};

type BoardMember = {
  firstname: string;
  lastname: string;
  personalCode: string;
  citizenship: string;
  birthday: Date | null;
  experience: number;
};

type BeneficialOwner = {
  firstname: string;
  lastname: string;
  personalCode: string;
  citizenship: string;
  birthday: Date | null;
};

type PoliticalMember = {
  firstname: string;
  lastname: string;
  citizenship: string; // Added for new persons
  personalCode: string; // Added for new persons' personal code
  birthday: Date | null;
  institution: string; // Changed from roleOrPosition
  position: string; // Changed from timePeriod
  relationship: string; // Relationship type
  isConfirmed: boolean;
};

type FormErrors = {
  companyName?: string;
  registryCode?: string;
  telephone?: string;
  email?: string;
  // Legal Address errors
  legalAddress?: string;
  legalCity?: string;
  legalCounty?: string;
  legalCountry?: string;
  legalZipCode?: string;
  // Business Address errors
  businessAddress?: string;
  businessCity?: string;
  businessCounty?: string;
  businessCountry?: string;
  businessZipCode?: string;
  activity?: string;
  activityAreas?: string;
  mainPartners?: string;
  reasonOfNoBO?: string;
  // Edit form errors
  editFirstname?: string;
  editLastname?: string;
  editPersonalCode?: string;
  editCitizenship?: string;
  editBirthday?: string;
  editExperience?: string;
  editBOFirstname?: string;
  editBOLastname?: string;
  editBOPersonalCode?: string;
  editBOCitizenship?: string;
  editBOBirthday?: string;
  // Political member errors
  politicalFirstname?: string;
  politicalLastname?: string;
  politicalPersonalCode?: string;
  politicalCitizenship?: string;
  politicalBirthday?: string;
  politicalInstitution?: string;
  politicalPosition?: string;
  politicalRelationship?: string;
};

function KYCForm() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState<number>(1);
  
  // JWT token state
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [cid, setCid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Countries data state
  const [countries, setCountries] = useState<Country[]>([]);
  const [countriesLoading, setCountriesLoading] = useState<boolean>(true);
  
  // Form will be loaded from API endpoint
  const [form, setForm] = useState<FormState>({
    companyName: '',
    registryCode: '',
    telephone: '',
    email: '',
    legalAddress: '',
    legalCity: '',
    legalCounty: '',
    legalCountry: '',
    legalZipCode: '',
    businessAddress: '',
    businessCity: '',
    businessCounty: '',
    businessCountry: '',
    businessZipCode: '',
    differentAddress: false,
    activity: '',
    activityText: '',
    activityDescription: '',
    activityAreas: [],
    mainPartners: '',
    vatNumber: '',
    boardMembers: [],
    beneficialOwners: [],
    beneficialOwnersObligatory: true,
    reasonOfNoBO: '',
    hasPoliticalMembers: false,
    politicalMembers: [],
  });


  const [errors, setErrors] = useState<FormErrors>({});
  const [editErrors, setEditErrors] = useState<FormErrors>({});
  const [editingMember, setEditingMember] = useState<number | null>(null);
  const [isAddingMember, setIsAddingMember] = useState<boolean>(false);
  const [editForm, setEditForm] = useState<BoardMember>({
    firstname: '',
    lastname: '',
    personalCode: '',
    citizenship: '',
    birthday: null,
    experience: 0
  });

  // Experience input states (years and months)
  const [editExperienceYears, setEditExperienceYears] = useState<number>(0);
  const [editExperienceMonths, setEditExperienceMonths] = useState<number>(0);

  // Step 4 states
  const [editingBO, setEditingBO] = useState<number | null>(null);
  const [isAddingBO, setIsAddingBO] = useState<boolean>(false);
  const [editBOForm, setEditBOForm] = useState<BeneficialOwner>({
    firstname: '',
    lastname: '',
    personalCode: '',
    citizenship: '',
    birthday: null
  });
  const [isAddingNewPoliticalMember, setIsAddingNewPoliticalMember] = useState<boolean>(false);
  const [showExtendedDefinition, setShowExtendedDefinition] = useState<boolean>(false);
  const [showExtendedDefinitionStep5, setShowExtendedDefinitionStep5] = useState<boolean>(false);
  const [showPoliticalPersonsList, setShowPoliticalPersonsList] = useState<boolean>(false);
  const [showPersonSelectionModal, setShowPersonSelectionModal] = useState<boolean>(false);
  const [selectedPersonForPolitical, setSelectedPersonForPolitical] = useState<BeneficialOwner | null>(null);
  const [editingPoliticalMember, setEditingPoliticalMember] = useState<number | null>(null);
  const [editPoliticalForm, setEditPoliticalForm] = useState<PoliticalMember>({
    firstname: '',
    lastname: '',
    personalCode: '',
    citizenship: '',
    birthday: null,
    institution: '',
    position: '',
    relationship: '',
    isConfirmed: false
  });
  
  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'conflict' | 'error'>('idle');
  
  // Activity selection modal state
  const [showActivityModal, setShowActivityModal] = useState(false);
  
  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showActivityModal && !(event.target as Element).closest('.activity-modal')) {
        setShowActivityModal(false);
      }
    };

    if (showActivityModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActivityModal]);

  // JWT exchange logic - runs once on component mount
  useEffect(() => {
    const exchangeJWT = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const key = urlParams.get('key');
        const signature = urlParams.get('signature');
        const cidParam = urlParams.get('cid');
        
        // Check if required parameters exist
        if (!key || !signature || !cidParam) {
          console.log('Missing required URL parameters, redirecting to:', REDIRECT_URL);
          // Add delay to see console output before redirect
          setTimeout(() => {
            window.location.href = REDIRECT_URL;
          }, 5000); // 5 second delay
          return;
        }
        
        // Store CID for later use
        setCid(cidParam);
        
        // Exchange API key and signature for JWT
        const response = await fetch(AUTH_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: key,
            signature: signature
          })
        });
        
        const result = await response.json();
        console.log('JWT exchange result:', result);
        
        if (result.success && result.data?.tokens?.accessToken) {
          const accessToken = result.data.tokens.accessToken;
          setJwtToken(accessToken);
          console.log('JWT access token obtained successfully');
          setIsLoading(false);
        } else {
          console.error('Failed to obtain JWT token:', result);
          // Add delay to see console output before redirect
          setTimeout(() => {
            window.location.href = REDIRECT_URL;
          }, 5000); // 5 second delay
        }
      } catch (error) {
        console.error('Error during JWT exchange:', error);
        // Add delay to see console output before redirect
        setTimeout(() => {
          window.location.href = REDIRECT_URL;
        }, 5000); // 5 second delay
      }
    };
    
    exchangeJWT();
  }, []);

  // Load default data from endpoint (using sample data for now)
  useEffect(() => {
    const loadDefaultData = async () => {
      try {
        // Check if there's saved data in localStorage
        const savedFormData = localStorage.getItem('kycFormData');
        const savedStep = localStorage.getItem('kycCurrentStep');
        const savedExpiration = localStorage.getItem('kycFormExpiration');
        
        // Check if data exists and hasn't expired
        const isExpired = savedExpiration ? new Date().getTime() > parseInt(savedExpiration) : true;
        
        if (savedFormData && savedStep && !isExpired) {
          // Load saved data
          const parsedData = JSON.parse(savedFormData);
          
          // Convert date strings back to Date objects and ensure new address fields exist
          const processedData = {
            ...parsedData,
            // Ensure new address fields exist with default values
            legalCity: parsedData.legalCity || '',
            legalCounty: parsedData.legalCounty || '',
            legalCountry: parsedData.legalCountry || '',
            legalZipCode: parsedData.legalZipCode || '',
            businessCity: parsedData.businessCity || '',
            businessCounty: parsedData.businessCounty || '',
            businessCountry: parsedData.businessCountry || '',
            businessZipCode: parsedData.businessZipCode || '',
            vatNumber: parsedData.vatNumber || '',
            boardMembers: parsedData.boardMembers.map((member: any) => ({
              ...member,
              birthday: member.birthday ? new Date(member.birthday) : null
            })),
            beneficialOwners: parsedData.beneficialOwners.map((owner: any) => ({
              ...owner,
              birthday: owner.birthday ? new Date(owner.birthday) : null
            })),
            politicalMembers: (parsedData.politicalMembers || []).map((pm: any) => ({
              ...pm,
              birthday: pm.birthday ? new Date(pm.birthday) : null,
              relationship: pm.relationship || 'beneficial_owner' // Default relationship if not set
            }))
          };
          
          setForm(processedData);
          setStep(parseInt(savedStep));
        } else {
          // Load data from API
          try {
            // Check if we have JWT token and CID
            if (!jwtToken || !cid) {
              console.log('Waiting for JWT token and CID...');
              return;
            }
            
            const response = await fetch(`${KYC_API_BASE_URL}/company/${cid}`, {
              headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json'
              }
            });
            const result = await response.json();
            
            if (result.success && result.data) {
              const data = result.data;
              
              // Convert date strings to Date objects and process the data
              const processedData = {
                companyName: data.companyName || '',
                registryCode: data.registryCode || '',
                telephone: data.telephone || '',
                email: data.email || '',
                legalAddress: data.legalAddress || '',
                legalCity: data.legalCity || '',
                legalCounty: data.legalCounty || '',
                legalCountry: data.legalCountry || '',
                legalZipCode: data.legalZipCode || '',
                businessAddress: data.businessAddress || '',
                businessCity: data.businessCity || '',
                businessCounty: data.businessCounty || '',
                businessCountry: data.businessCountry || '',
                businessZipCode: data.businessZipCode || '',
                differentAddress: data.differentAddress === 'true' || data.differentAddress === true,
                activity: data.activity || '',
                activityText: data.activityText || '',
                activityDescription: data.activityDescription || '',
                activityAreas: data.activityAreas || [],
                mainPartners: data.mainPartners || '',
                vatNumber: data.vatNumber || '',
                boardMembers: (data.boardMembers || []).map((member: any) => {
                  let birthday = member.birthday ? new Date(member.birthday) : null;
                  
                  // Auto-extract birthday from Estonian personal code if citizenship is Estonia
                  if (!birthday && member.personalCode && member.citizenship?.toLowerCase() === 'ee' && member.personalCode.length === 11) {
                    birthday = extractBirthdayFromEstonianCode(member.personalCode);
                  }
                  
                  return {
                    firstname: member.firstname || '',
                    lastname: member.lastname || '',
                    personalCode: member.personalCode || '',
                    citizenship: member.citizenship || '',
                    birthday: birthday,
                    experience: member.experience || 0
                  };
                }),
                beneficialOwners: (data.beneficialOwners || []).map((owner: any) => {
                  let birthday = owner.birthday ? new Date(owner.birthday) : null;
                  
                  // Auto-extract birthday from Estonian personal code if citizenship is Estonia
                  if (!birthday && owner.personalCode && owner.citizenship?.toLowerCase() === 'ee' && owner.personalCode.length === 11) {
                    birthday = extractBirthdayFromEstonianCode(owner.personalCode);
                  }
                  
                  return {
                    firstname: owner.firstname || '',
                    lastname: owner.lastname || '',
                    personalCode: owner.personalCode || '',
                    citizenship: owner.citizenship || '',
                    birthday: birthday
                  };
                }),
                beneficialOwnersObligatory: data.beneficialOwnersObligatory !== false,
                reasonOfNoBO: data.reasonOfNoBO || '',
                hasPoliticalMembers: data.hasPoliticalMembers || false,
                politicalMembers: (data.politicalMembers || []).map((pm: any) => {
                  let birthday = pm.birthday ? new Date(pm.birthday) : null;
                  
                  // Auto-extract birthday from Estonian personal code if citizenship is Estonia
                  if (!birthday && pm.personalCode && pm.citizenship?.toLowerCase() === 'ee' && pm.personalCode.length === 11) {
                    birthday = extractBirthdayFromEstonianCode(pm.personalCode);
                  }
                  
                  return {
                    ...pm,
                    birthday: birthday,
                    relationship: pm.relationship || 'beneficial_owner' // Default relationship if not set
                  };
                })
              };
              
              setForm(processedData);
            } else {
              console.error('API response not successful:', result);
            }
          } catch (error) {
            console.error('Error loading data from API:', error);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadDefaultData();
  }, [jwtToken, cid]);

  // Save form data to localStorage whenever form or step changes
  useEffect(() => {
    // Don't save on initial load or on thank you page (step 7)
    if ((form.companyName === '' && form.registryCode === '') || step === 7) {
      return;
    }
    
    try {
      // Convert Date objects to strings for localStorage
      const dataToSave = {
        ...form,
        boardMembers: form.boardMembers.map(member => ({
          ...member,
          birthday: member.birthday ? member.birthday.toISOString() : null
        })),
        beneficialOwners: form.beneficialOwners.map(owner => ({
          ...owner,
          birthday: owner.birthday ? owner.birthday.toISOString() : null
        }))
      };
      
      // Set expiration to 24 hours from now
      const expirationTime = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
      
      localStorage.setItem('kycFormData', JSON.stringify(dataToSave));
      localStorage.setItem('kycCurrentStep', step.toString());
      localStorage.setItem('kycFormExpiration', expirationTime.toString());
    } catch (error) {
      console.error('Error saving form data to localStorage:', error);
    }
  }, [form, step]);

  // Log the entire KYC form object when reaching Step 6
  useEffect(() => {
    if (step === 6) {
      console.log('KYC Form Object:', form);
    }
  }, [step, form]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{7,}$/;
    return phoneRegex.test(phone);
  };

  const validateRegistryCode = (code: string): boolean => {
    const codeRegex = /^[0-9]{7,8}$/;
    return codeRegex.test(code);
  };

  const validateName = (name: string): boolean => {
    // Name validation: only letters, spaces, hyphens, apostrophes, and common name characters
    // Allows for names like: John, Mary-Jane, O'Connor, van der Berg, etc.
    return /^[a-zA-ZÀ-ÿ\u00C0-\u017F\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s\-'\.]+$/.test(name.trim());
  };

  const validateEstonianPersonalCode = (personalCode: string): boolean => {
    // Estonian personal code format: GYYMMDDXXXXX (11 digits)
    if (!/^\d{11}$/.test(personalCode)) return false;
    
    const gender = parseInt(personalCode[0]!, 10);
    const year = parseInt(personalCode.substring(1, 3), 10);
    const month = parseInt(personalCode.substring(3, 5), 10);
    const day = parseInt(personalCode.substring(5, 7), 10);
    
    // Determine century based on gender digit
    let fullYear: number;
    if (gender === 1 || gender === 2) {
      fullYear = 1800 + year;
    } else if (gender === 3 || gender === 4) {
      fullYear = 1900 + year;
    } else if (gender === 5 || gender === 6) {
      fullYear = 2000 + year;
    } else {
      return false; // Invalid gender digit
    }
    
    // Additional quick check for month and day range (fast fail)
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return false;
    }
    
    // Build date and verify validity
    const date = new Date(fullYear, month - 1, day);
    
    if (
      date.getFullYear() === fullYear &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return true;
    }
    
    return false;
  };

  const extractBirthdayFromEstonianCode = (personalCode: string): Date | null => {
    // Estonian personal code format: GYYMMDDXXXXX (11 digits)
    if (!/^\d{11}$/.test(personalCode)) return null;

    const gender = parseInt(personalCode[0]!, 10);
    const year = parseInt(personalCode.substring(1, 3), 10);
    const month = parseInt(personalCode.substring(3, 5), 10);
    const day = parseInt(personalCode.substring(5, 7), 10);

    // Determine century based on gender digit
    let fullYear: number;
    if (gender === 1 || gender === 2) {
      fullYear = 1800 + year;
    } else if (gender === 3 || gender === 4) {
      fullYear = 1900 + year;
    } else if (gender === 5 || gender === 6) {
      fullYear = 2000 + year;
    } else {
      return null; // Invalid gender digit
    }

    // Additional quick check for month and day range (fast fail)
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }

    // Build date and verify validity
    const date = new Date(fullYear, month - 1, day);

    if (
      date.getFullYear() === fullYear &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return date;
    }

    return null;
  };

  const validateStep = (): boolean => {
    let errs: FormErrors = {};
    
    if (step === 1) {
      
      if (!form.companyName?.trim()) {
        errs.companyName = t('kyc.step1.validation.required');
      }
      
      if (!form.registryCode?.trim()) {
        errs.registryCode = t('kyc.step1.validation.required');
      } else if (!validateRegistryCode(form.registryCode)) {
        errs.registryCode = t('kyc.step1.validation.invalidRegistryCode');
      }
      
      if (!form.telephone?.trim()) {
        errs.telephone = t('kyc.step1.validation.required');
      } else if (!validatePhone(form.telephone)) {
        errs.telephone = t('kyc.step1.validation.invalidPhone');
      }
      
      if (!form.email?.trim()) {
        errs.email = t('kyc.step1.validation.required');
      } else if (!validateEmail(form.email)) {
        errs.email = t('kyc.step1.validation.invalidEmail');
      }
      
      // Legal Address validation
      if (!form.legalAddress?.trim()) {
        errs.legalAddress = t('kyc.step1.validation.required');
      }
      if (!form.legalCity?.trim()) {
        errs.legalCity = t('kyc.step1.validation.required');
      }
      if (!form.legalCounty?.trim()) {
        errs.legalCounty = t('kyc.step1.validation.required');
      }
      if (!form.legalCountry?.trim()) {
        errs.legalCountry = t('kyc.step1.validation.required');
      }
      if (!form.legalZipCode?.trim()) {
        errs.legalZipCode = t('kyc.step1.validation.required');
      }
      
      // Business Address validation (only if different address is checked)
      if (form.differentAddress) {
        if (!form.businessAddress?.trim()) {
          errs.businessAddress = t('kyc.step1.validation.required');
        }
        if (!form.businessCity?.trim()) {
          errs.businessCity = t('kyc.step1.validation.required');
        }
        if (!form.businessCounty?.trim()) {
          errs.businessCounty = t('kyc.step1.validation.required');
        }
        if (!form.businessCountry?.trim()) {
          errs.businessCountry = t('kyc.step1.validation.required');
        }
        if (!form.businessZipCode?.trim()) {
          errs.businessZipCode = t('kyc.step1.validation.required');
        }
      }
    }
    
    if (step === 2) {
      if (!form.activity && !form.activityText?.trim()) {
        errs.activity = t('kyc.step2.validation.selectActivity');
      }
      
      if (!form.activityAreas || form.activityAreas.length === 0) {
        errs.activityAreas = t('kyc.step2.validation.selectRegions');
      }
      
      if (!form.mainPartners.trim()) {
        errs.mainPartners = t('kyc.step2.validation.enterPartners');
      }
    }
    
    if (step === 4) {
      if (!form.beneficialOwnersObligatory && !form.reasonOfNoBO) {
        errs.reasonOfNoBO = t('kyc.step4.validation.selectReason');
      }
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (step === 6) {
      // Submit the form to API
      handleFormSubmission();
    } else {
      const isValid = validateStep();
      if (isValid) {
        setStep((s) => s + 1);
      }
    }
  };

  const handleFormSubmission = async () => {
    if (!jwtToken || !cid) {
      console.error('Missing JWT token or CID for form submission');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare the form data according to CreateKycInfoDto structure
      const formData = {
        companyName: form.companyName,
        registryCode: form.registryCode,
        telephone: form.telephone,
        email: form.email,
        legalAddress: form.legalAddress,
        legalCity: form.legalCity,
        legalCounty: form.legalCounty,
        legalCountry: form.legalCountry,
        legalZipCode: form.legalZipCode,
        businessAddress: form.businessAddress,
        businessCity: form.businessCity,
        businessCounty: form.businessCounty,
        businessCountry: form.businessCountry,
        businessZipCode: form.businessZipCode,
        differentAddress: form.differentAddress,
        activity: form.activity,
        activityText: form.activityText,
        activityDescription: form.activityDescription,
        activityAreas: form.activityAreas,
        mainPartners: form.mainPartners,
        boardMembers: form.boardMembers.map(member => ({
          firstname: member.firstname,
          lastname: member.lastname,
          personalCode: member.personalCode,
          citizenship: member.citizenship,
          birthday: member.birthday ? member.birthday.toISOString().split('T')[0] : null,
          experience: member.experience
        })),
        beneficialOwners: form.beneficialOwners.map(owner => ({
          firstname: owner.firstname,
          lastname: owner.lastname,
          personalCode: owner.personalCode,
          citizenship: owner.citizenship,
          birthday: owner.birthday ? owner.birthday.toISOString().split('T')[0] : null
        })),
        beneficialOwnersObligatory: form.beneficialOwnersObligatory,
        reasonOfNoBO: form.reasonOfNoBO,
        vatNumber: form.vatNumber,
        hasPoliticalMembers: form.hasPoliticalMembers,
                         politicalMembers: form.politicalMembers.map(pm => ({
          firstname: pm.firstname,
          lastname: pm.lastname,
          personalCode: pm.personalCode,
          citizenship: pm.citizenship,
          birthday: pm.birthday ? pm.birthday.toISOString().split('T')[0] : null,
          institution: pm.institution,
          position: pm.position,
          relationship: pm.relationship
        }))
      };

      console.log('Submitting form data:', formData);

      const response = await fetch(`${KYC_API_BASE_URL}/form/${cid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      console.log('Form submission response status:', response.status);

      if (response.status === 201) {
        // Success - clear localStorage and show success page
        try {
          localStorage.removeItem('kycFormData');
          localStorage.removeItem('kycCurrentStep');
          localStorage.removeItem('kycFormExpiration');
        } catch (error) {
          console.error('Error clearing localStorage:', error);
        }
        setSubmissionStatus('success');
        setStep(7);
      } else if (response.status === 409) {
        // Conflict - form already submitted
        setSubmissionStatus('conflict');
        setStep(7);
      } else {
        // Error - show error page
        setSubmissionStatus('error');
        setStep(7);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus('error');
      setStep(7);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep((s) => s - 1);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateStep()) return;
    // Handle final submission
    console.log('Form submitted:', form);
  };

  const changeLang = (lng: string) => i18n.changeLanguage(lng);

  // Utility function to clear saved form data (for testing)
  const clearSavedData = () => {
    try {
      localStorage.removeItem('kycFormData');
      localStorage.removeItem('kycCurrentStep');
      localStorage.removeItem('kycFormExpiration');
      window.location.reload(); // Reload to start fresh
    } catch (error) {
      console.error('Error clearing saved data:', error);
    }
  };

  // Utility function to check if saved data is expired
  const isDataExpired = () => {
    try {
      const savedExpiration = localStorage.getItem('kycFormExpiration');
      if (!savedExpiration) return true;
      return new Date().getTime() > parseInt(savedExpiration);
    } catch (error) {
      console.error('Error checking data expiration:', error);
      return true;
    }
  };

  // Utility function to clear expired data
  const clearExpiredData = () => {
    if (isDataExpired()) {
      clearSavedData();
    }
  };

  // Utility function to check localStorage status
  const checkLocalStorageStatus = () => {
    try {
      const hasData = localStorage.getItem('kycFormData') !== null;
      const hasStep = localStorage.getItem('kycCurrentStep') !== null;
      const hasExpiration = localStorage.getItem('kycFormExpiration') !== null;
      const isExpired = isDataExpired();
      
      console.log('localStorage Status:', {
        hasData,
        hasStep,
        hasExpiration,
        isExpired,
        currentStep: localStorage.getItem('kycCurrentStep'),
        expiration: localStorage.getItem('kycFormExpiration')
      });
      
      return { hasData, hasStep, hasExpiration, isExpired };
    } catch (error) {
      console.error('Error checking localStorage status:', error);
      return { hasData: false, hasStep: false, hasExpiration: false, isExpired: true };
    }
  };

  // Load countries data based on current language
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        const countriesData = await getCountriesData(i18n.language);
        setCountries(countriesData);
      } catch (error) {
        console.error('Error loading countries data:', error);
        // Fallback to empty array
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    };
    
    loadCountries();
  }, [i18n.language]);
  
  const countryOptions = countries.map(country => ({
    value: country.alpha2,
    label: country.name
  }));

  // Exemption reason options for beneficial owners
  const exemptionReasonOptions = [
    { value: 'korterühistu', label: 'Korterühistu' },
    { value: 'hooneühistu', label: 'Hooneühistu' },
    { value: 'börsilettevõte', label: 'Börsilettevõte' },
    { value: 'sihtasutus', label: 'Sihtasutus' },
    { value: 'filiaal', label: 'Filiaal' }
  ];

  const handleActivityChange = (selectedOption: any) => {
    setForm(prev => ({ 
      ...prev, 
      activity: selectedOption.value,
      activityText: selectedOption.label
    }));
    if (errors.activity) {
      setErrors(prev => ({ ...prev, activity: undefined }));
    }
  };

  const handleCountriesChange = (selectedOptions: any) => {
    const selectedValues = selectedOptions ? selectedOptions.map((option: any) => option.value) : [];
    setForm(prev => ({ ...prev, activityAreas: selectedValues }));
    if (errors.activityAreas) {
      setErrors(prev => ({ ...prev, activityAreas: undefined }));
    }
  };

  const handleEditMember = (index: number) => {
    if (isAddingMember) return; // Prevent editing while adding
    setEditingMember(index);
    setEditForm(form.boardMembers[index]);
    
    // Initialize years and months from total months
    const { years, months } = convertMonthsToYearsAndMonths(form.boardMembers[index].experience);
    setEditExperienceYears(years);
    setEditExperienceMonths(months);
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setEditForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null,
      experience: 0
    });
    setEditExperienceYears(0);
    setEditExperienceMonths(0);
  };

  const handleSaveEdit = () => {
    let hasErrors = false;
    const newErrors: FormErrors = {};
    
    // Validate all required fields
    if (!editForm.firstname.trim()) {
      newErrors.editFirstname = t('kyc.step3.validation.required');
      hasErrors = true;
    } else if (!validateName(editForm.firstname)) {
      newErrors.editFirstname = t('kyc.step3.validation.invalidName');
      hasErrors = true;
    }
    
    if (!editForm.lastname.trim()) {
      newErrors.editLastname = t('kyc.step3.validation.required');
      hasErrors = true;
    } else if (!validateName(editForm.lastname)) {
      newErrors.editLastname = t('kyc.step3.validation.invalidName');
      hasErrors = true;
    }
    
    // Personal code is only mandatory for Estonian citizens
    if (editForm.citizenship?.toLowerCase() === 'ee') {
      if (!editForm.personalCode.trim()) {
        newErrors.editPersonalCode = t('kyc.step3.validation.required');
        hasErrors = true;
      } else if (!validateEstonianPersonalCode(editForm.personalCode)) {
        newErrors.editPersonalCode = t('kyc.step3.validation.invalidPersonalCode');
        hasErrors = true;
      }
    }
    
    if (!editForm.citizenship) {
      newErrors.editCitizenship = t('kyc.step3.validation.required');
      hasErrors = true;
    }
    
    if (!editForm.birthday) {
      newErrors.editBirthday = t('kyc.step3.validation.required');
      hasErrors = true;
    }
    
    if (editForm.experience < 0) {
      newErrors.editExperience = t('kyc.step3.validation.invalidExperience');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setEditErrors(newErrors);
      return;
    }
    
    // Clear errors on successful save
    setEditErrors({});
    
    setForm(prev => ({
      ...prev,
      boardMembers: prev.boardMembers.map((member, index) => 
        index === editingMember ? editForm : member
      )
    }));
    setEditingMember(null);
  };

  const handleEditFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: name === 'experience' ? parseInt(value) || 0 : value
    }));
    
    // Clear errors when user starts typing
    if (name === 'firstname' && editErrors.editFirstname) {
      setEditErrors(prev => ({ ...prev, editFirstname: undefined }));
    }
    if (name === 'lastname' && editErrors.editLastname) {
      setEditErrors(prev => ({ ...prev, editLastname: undefined }));
    }
    if (name === 'personalCode' && editErrors.editPersonalCode) {
      setEditErrors(prev => ({ ...prev, editPersonalCode: undefined }));
    }
    if (name === 'experience' && editErrors.editExperience) {
      setEditErrors(prev => ({ ...prev, editExperience: undefined }));
    }
    
    // Auto-extract birthday from Estonian personal code
    if (name === 'personalCode' && value.length === 11 && editForm.citizenship === 'ee') {
      const extractedBirthday = extractBirthdayFromEstonianCode(value);
      if (extractedBirthday) {
        setEditForm(prev => ({
          ...prev,
          birthday: extractedBirthday
        }));
        // Clear birthday error if it was set
        if (editErrors.editBirthday) {
          setEditErrors(prev => ({ ...prev, editBirthday: undefined }));
        }
      }
    }
  };

  const handleDateChange = (date: Date | null) => {
    setEditForm(prev => ({
      ...prev,
      birthday: date
    }));
  };

  // Handle experience years input change
  const handleExperienceYearsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const years = parseInt(e.target.value) || 0;
    setEditExperienceYears(years);
    
    // Convert to total months and update the form
    const totalMonths = convertYearsAndMonthsToMonths(years, editExperienceMonths);
    setEditForm(prev => ({
      ...prev,
      experience: totalMonths
    }));
    
    // Clear errors
    if (editErrors.editExperience) {
      setEditErrors(prev => ({ ...prev, editExperience: undefined }));
    }
  };

  // Handle experience months input change
  const handleExperienceMonthsChange = (e: ChangeEvent<HTMLInputElement>) => {
    const months = parseInt(e.target.value) || 0;
    setEditExperienceMonths(months);
    
    // Convert to total months and update the form
    const totalMonths = convertYearsAndMonthsToMonths(editExperienceYears, months);
    setEditForm(prev => ({
      ...prev,
      experience: totalMonths
    }));
    
    // Clear errors
    if (editErrors.editExperience) {
      setEditErrors(prev => ({ ...prev, editExperience: undefined }));
    }
  };

  const handleBODateChange = (date: Date | null) => {
    setEditBOForm(prev => ({
      ...prev,
      birthday: date
    }));
  };

  // Handle opening person selection modal
  const handleOpenPersonSelectionModal = () => {
    setShowPersonSelectionModal(true);
  };

  // Handle closing person selection modal
  const handleClosePersonSelectionModal = () => {
    setShowPersonSelectionModal(false);
    setSelectedPersonForPolitical(null);
  };

  // Handle selecting a person from the modal
  const handleSelectPersonFromModal = (person: BeneficialOwner) => {
    setSelectedPersonForPolitical(person);
  };

  // Handle confirming the selected person
  const handleConfirmSelectedPerson = () => {
    if (selectedPersonForPolitical) {
      handleAddPoliticalMember(selectedPersonForPolitical);
      handleClosePersonSelectionModal();
    }
  };

  // Handle editing political member
  const handleEditPoliticalMember = (index: number) => {
    setEditingPoliticalMember(index);
    setEditPoliticalForm(form.politicalMembers[index]);
  };

  // Handle canceling political member edit
  const handleCancelPoliticalEdit = () => {
    setEditingPoliticalMember(null);
    setEditPoliticalForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null,
      institution: '',
      position: '',
      relationship: '',
      isConfirmed: false
    });
  };

  // Handle saving political member edit
  const handleSavePoliticalEdit = () => {
    if (editingPoliticalMember !== null) {
      const updatedMembers = [...form.politicalMembers];
      updatedMembers[editingPoliticalMember] = editPoliticalForm;
      setForm(prev => ({
        ...prev,
        politicalMembers: updatedMembers
      }));
      handleCancelPoliticalEdit();
    }
  };

  // Handle political member form change
  const handlePoliticalEditFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditPoliticalForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCitizenshipChange = (selectedOption: any) => {
    setEditForm(prev => ({
      ...prev,
      citizenship: selectedOption.value
    }));
    
    // Clear citizenship error when user selects
    if (editErrors.editCitizenship) {
      setEditErrors(prev => ({ ...prev, editCitizenship: undefined }));
    }
    
    // Auto-extract birthday if citizenship is Estonia and personal code is already entered
    if (selectedOption.value === 'ee' && editForm.personalCode.length === 11) {
      const extractedBirthday = extractBirthdayFromEstonianCode(editForm.personalCode);
      if (extractedBirthday) {
        setEditForm(prev => ({
          ...prev,
          birthday: extractedBirthday
        }));
        // Clear birthday error if it was set
        if (editErrors.editBirthday) {
          setEditErrors(prev => ({ ...prev, editBirthday: undefined }));
        }
      }
    }
  };

  const handleBOCitizenshipChange = (selectedOption: any) => {
    setEditBOForm(prev => ({
      ...prev,
      citizenship: selectedOption ? selectedOption.value : ''
    }));
    
    // Clear citizenship error when user selects
    if (editErrors.editBOCitizenship) {
      setEditErrors(prev => ({ ...prev, editBOCitizenship: undefined }));
    }
    
    // Auto-extract birthday if citizenship is Estonia and personal code is already entered
    if (selectedOption && selectedOption.value === 'ee' && editBOForm.personalCode.length === 11) {
      const extractedBirthday = extractBirthdayFromEstonianCode(editBOForm.personalCode);
      if (extractedBirthday) {
        setEditBOForm(prev => ({
          ...prev,
          birthday: extractedBirthday
        }));
        // Clear birthday error if it was set
        if (editErrors.editBOBirthday) {
          setEditErrors(prev => ({ ...prev, editBOBirthday: undefined }));
        }
      }
    }
  };

  const handleAddMember = () => {
    if (editingMember !== null) return; // Prevent adding while editing
    setIsAddingMember(true);
    setEditForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null,
      experience: 0
    });
    
    // Initialize years and months to 0
    setEditExperienceYears(0);
    setEditExperienceMonths(0);
  };

  const handleCancelAdd = () => {
    setIsAddingMember(false);
    setEditForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null,
      experience: 0
    });
    setEditExperienceYears(0);
    setEditExperienceMonths(0);
  };

  const handleSaveAdd = () => {
    let hasErrors = false;
    const newErrors: FormErrors = {};
    
    // Validate all required fields
    if (!editForm.firstname.trim()) {
      newErrors.editFirstname = t('kyc.step3.validation.required');
      hasErrors = true;
    } else if (!validateName(editForm.firstname)) {
      newErrors.editFirstname = t('kyc.step3.validation.invalidName');
      hasErrors = true;
    }
    
    if (!editForm.lastname.trim()) {
      newErrors.editLastname = t('kyc.step3.validation.required');
      hasErrors = true;
    } else if (!validateName(editForm.lastname)) {
      newErrors.editLastname = t('kyc.step3.validation.invalidName');
      hasErrors = true;
    }
    
    // Personal code is only mandatory for Estonian citizens
    if (editForm.citizenship?.toLowerCase() === 'ee') {
      if (!editForm.personalCode.trim()) {
        newErrors.editPersonalCode = t('kyc.step3.validation.required');
        hasErrors = true;
      } else if (!validateEstonianPersonalCode(editForm.personalCode)) {
        newErrors.editPersonalCode = t('kyc.step3.validation.invalidPersonalCode');
        hasErrors = true;
      }
    }
    
    if (!editForm.citizenship) {
      newErrors.editCitizenship = t('kyc.step3.validation.required');
      hasErrors = true;
    }
    
    if (!editForm.birthday) {
      newErrors.editBirthday = t('kyc.step3.validation.required');
      hasErrors = true;
    }
    
    if (editForm.experience < 0) {
      newErrors.editExperience = t('kyc.step3.validation.invalidExperience');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setEditErrors(newErrors);
      return;
    }
    
    // Clear errors on successful save
    setEditErrors({});
    
    setForm(prev => ({
      ...prev,
      boardMembers: [...prev.boardMembers, editForm]
    }));
    setIsAddingMember(false);
    setEditForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null,
      experience: 0
    });
  };

  const handleDeleteMember = (index: number) => {
    setForm(prev => ({
      ...prev,
      boardMembers: prev.boardMembers.filter((_, i) => i !== index)
    }));
  };

  // Step 4 handlers
  const handleBOObligatoryChange = (obligatory: boolean) => {
    setForm(prev => ({
      ...prev,
      beneficialOwnersObligatory: obligatory
    }));
  };

  const handleEditBO = (index: number) => {
    if (isAddingBO) return;
    setEditingBO(index);
    setEditBOForm(form.beneficialOwners[index]);
  };

  const handleCancelEditBO = () => {
    setEditingBO(null);
    setEditBOForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null
    });
  };

  const handleSaveEditBO = () => {
    let hasErrors = false;
    const newErrors: FormErrors = {};
    
    // Validate all required fields
    if (!editBOForm.firstname.trim()) {
      newErrors.editBOFirstname = t('kyc.step4.validation.required');
      hasErrors = true;
    } else if (!validateName(editBOForm.firstname)) {
      newErrors.editBOFirstname = t('kyc.step4.validation.invalidName');
      hasErrors = true;
    }
    
    if (!editBOForm.lastname.trim()) {
      newErrors.editBOLastname = t('kyc.step4.validation.required');
      hasErrors = true;
    } else if (!validateName(editBOForm.lastname)) {
      newErrors.editBOLastname = t('kyc.step4.validation.invalidName');
      hasErrors = true;
    }
    
    // Personal code is only mandatory for Estonian citizens
    if (editBOForm.citizenship?.toLowerCase() === 'ee') {
      if (!editBOForm.personalCode.trim()) {
        newErrors.editBOPersonalCode = t('kyc.step4.validation.required');
        hasErrors = true;
      } else if (!validateEstonianPersonalCode(editBOForm.personalCode)) {
        newErrors.editBOPersonalCode = t('kyc.step4.validation.invalidPersonalCode');
        hasErrors = true;
      }
    }
    
    if (!editBOForm.citizenship) {
      newErrors.editBOCitizenship = t('kyc.step4.validation.required');
      hasErrors = true;
    }
    
    if (!editBOForm.birthday) {
      newErrors.editBOBirthday = t('kyc.step4.validation.required');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setEditErrors(newErrors);
      return;
    }
    
    // Clear errors on successful save
    setEditErrors({});
    
    setForm(prev => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.map((bo, index) => 
        index === editingBO ? editBOForm : bo
      )
    }));
    setEditingBO(null);
  };

  const handleAddBO = () => {
    if (editingBO !== null) return;
    setIsAddingBO(true);
    setEditBOForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null
    });
  };

  const handleCancelAddBO = () => {
    setIsAddingBO(false);
    setEditBOForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null
    });
  };

  const handleSaveAddBO = () => {
    let hasErrors = false;
    const newErrors: FormErrors = {};
    
    // Validate all required fields
    if (!editBOForm.firstname.trim()) {
      newErrors.editBOFirstname = t('kyc.step4.validation.required');
      hasErrors = true;
    } else if (!validateName(editBOForm.firstname)) {
      newErrors.editBOFirstname = t('kyc.step4.validation.invalidName');
      hasErrors = true;
    }
    
    if (!editBOForm.lastname.trim()) {
      newErrors.editBOLastname = t('kyc.step4.validation.required');
      hasErrors = true;
    } else if (!validateName(editBOForm.lastname)) {
      newErrors.editBOLastname = t('kyc.step4.validation.invalidName');
      hasErrors = true;
    }
    
    // Personal code is only mandatory for Estonian citizens
    if (editBOForm.citizenship?.toLowerCase() === 'ee') {
      if (!editBOForm.personalCode.trim()) {
        newErrors.editBOPersonalCode = t('kyc.step4.validation.required');
        hasErrors = true;
      } else if (!validateEstonianPersonalCode(editBOForm.personalCode)) {
        newErrors.editBOPersonalCode = t('kyc.step4.validation.invalidPersonalCode');
        hasErrors = true;
      }
    }
    
    if (!editBOForm.citizenship) {
      newErrors.editBOCitizenship = t('kyc.step4.validation.required');
      hasErrors = true;
    }
    
    if (!editBOForm.birthday) {
      newErrors.editBOBirthday = t('kyc.step4.validation.required');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setEditErrors(newErrors);
      return;
    }
    
    // Clear errors on successful save
    setEditErrors({});
    
    setForm(prev => ({
      ...prev,
      beneficialOwners: [...prev.beneficialOwners, editBOForm]
    }));
    setIsAddingBO(false);
    setEditBOForm({
      firstname: '',
      lastname: '',
      personalCode: '',
      citizenship: '',
      birthday: null
    });
  };

  const handleDeleteBO = (index: number) => {
    setForm(prev => ({
      ...prev,
      beneficialOwners: prev.beneficialOwners.filter((_, i) => i !== index)
    }));
  };

  const handleEditBOFormChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditBOForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (name === 'firstname' && editErrors.editBOFirstname) {
      setEditErrors(prev => ({ ...prev, editBOFirstname: undefined }));
    }
    if (name === 'lastname' && editErrors.editBOLastname) {
      setEditErrors(prev => ({ ...prev, editBOLastname: undefined }));
    }
    if (name === 'personalCode' && editErrors.editBOPersonalCode) {
      setEditErrors(prev => ({ ...prev, editBOPersonalCode: undefined }));
    }
    
    // Auto-extract birthday from Estonian personal code
    if (name === 'personalCode' && value.length === 11 && editBOForm.citizenship === 'ee') {
      const extractedBirthday = extractBirthdayFromEstonianCode(value);
      if (extractedBirthday) {
        setEditBOForm(prev => ({
          ...prev,
          birthday: extractedBirthday
        }));
        // Clear birthday error if it was set
        if (editErrors.editBOBirthday) {
          setEditErrors(prev => ({ ...prev, editBOBirthday: undefined }));
        }
      }
    }
  };

  // Step 5 handlers
  const handlePoliticalMembersChange = (hasPolitical: boolean) => {
    setForm(prev => ({
      ...prev,
      hasPoliticalMembers: hasPolitical,
      politicalMembers: hasPolitical ? prev.politicalMembers : []
    }));
  };

  const handleAddPoliticalMember = (beneficialOwner: BeneficialOwner) => {
    const newPoliticalMember: PoliticalMember = {
      firstname: beneficialOwner.firstname,
      lastname: beneficialOwner.lastname,
      citizenship: beneficialOwner.citizenship,
      personalCode: beneficialOwner.personalCode,
      birthday: beneficialOwner.birthday,
      institution: '',
      position: '',
      relationship: 'beneficial_owner', // Default to "Tegelik kasusaaja, omanik või esindaja on ise riikliku taustaga isik"
      isConfirmed: false
    };
    
    setForm(prev => ({
      ...prev,
      politicalMembers: [...prev.politicalMembers, newPoliticalMember]
    }));
  };

  const handleAddNewPoliticalMember = () => {
    const newPoliticalMember: PoliticalMember = {
      firstname: '',
      lastname: '',
      citizenship: '',
      personalCode: '',
      birthday: null,
      institution: '',
      position: '',
      relationship: 'beneficial_owner', // Default to "Tegelik kasusaaja, omanik või esindaja on ise riikliku taustaga isik"
      isConfirmed: false
    };
    
    setForm(prev => ({
      ...prev,
      politicalMembers: [...prev.politicalMembers, newPoliticalMember]
    }));
  };

  const handleRemovePoliticalMember = (index: number) => {
    setForm(prev => ({
      ...prev,
      politicalMembers: prev.politicalMembers.filter((_, i) => i !== index)
    }));
  };

  const handlePoliticalMemberChange = (index: number, field: keyof PoliticalMember, value: string | Date | null) => {
    setForm(prev => ({
      ...prev,
      politicalMembers: prev.politicalMembers.map((pm, i) => 
        i === index ? { ...pm, [field]: value } : pm
      )
    }));
    
    // Clear errors when user starts typing
    if (field === 'firstname' && editErrors.politicalFirstname) {
      setEditErrors(prev => ({ ...prev, politicalFirstname: undefined }));
    }
    if (field === 'lastname' && editErrors.politicalLastname) {
      setEditErrors(prev => ({ ...prev, politicalLastname: undefined }));
    }
    if (field === 'personalCode' && editErrors.politicalPersonalCode) {
      setEditErrors(prev => ({ ...prev, politicalPersonalCode: undefined }));
    }
    if (field === 'citizenship' && editErrors.politicalCitizenship) {
      setEditErrors(prev => ({ ...prev, politicalCitizenship: undefined }));
    }
    if (field === 'institution' && editErrors.politicalInstitution) {
      setEditErrors(prev => ({ ...prev, politicalInstitution: undefined }));
    }
    if (field === 'position' && editErrors.politicalPosition) {
      setEditErrors(prev => ({ ...prev, politicalPosition: undefined }));
    }
    if (field === 'relationship' && editErrors.politicalRelationship) {
      setEditErrors(prev => ({ ...prev, politicalRelationship: undefined }));
    }
    
    // Auto-extract birthday from Estonian personal code for new political members
    if (field === 'personalCode' && typeof value === 'string' && value.length === 11) {
      const politicalMember = form.politicalMembers[index];
      if (politicalMember && politicalMember.citizenship?.toLowerCase() === 'ee') {
        const extractedBirthday = extractBirthdayFromEstonianCode(value);
        if (extractedBirthday) {
          setForm(prev => ({
            ...prev,
            politicalMembers: prev.politicalMembers.map((pm, i) => 
              i === index ? { ...pm, birthday: extractedBirthday } : pm
            )
          }));
        }
      }
    }
    
    // Auto-extract birthday when citizenship is changed to Estonia and personal code exists
    if (field === 'citizenship' && typeof value === 'string' && value.toLowerCase() === 'ee') {
      const politicalMember = form.politicalMembers[index];
      if (politicalMember && politicalMember.personalCode && politicalMember.personalCode.length === 11) {
        const extractedBirthday = extractBirthdayFromEstonianCode(politicalMember.personalCode);
        if (extractedBirthday) {
          setForm(prev => ({
            ...prev,
            politicalMembers: prev.politicalMembers.map((pm, i) => 
              i === index ? { ...pm, birthday: extractedBirthday } : pm
            )
          }));
        }
      }
    }
  };

  const handleConfirmPoliticalMember = (index: number) => {
    const politicalMember = form.politicalMembers[index];
    
    if (!politicalMember) return;
    
    let hasErrors = false;
    const newErrors: FormErrors = {};
    
    // Validate all required fields for new political members
    if (!politicalMember.isConfirmed) {
      if (!(politicalMember.firstname || '').trim()) {
        newErrors.politicalFirstname = t('kyc.step5.validation.required');
        hasErrors = true;
      } else if (!validateName(politicalMember.firstname || '')) {
        newErrors.politicalFirstname = t('kyc.step5.validation.invalidName');
        hasErrors = true;
      }
      
      if (!(politicalMember.lastname || '').trim()) {
        newErrors.politicalLastname = t('kyc.step5.validation.required');
        hasErrors = true;
      } else if (!validateName(politicalMember.lastname || '')) {
        newErrors.politicalLastname = t('kyc.step5.validation.invalidName');
        hasErrors = true;
      }
      
      // Personal code is only mandatory for Estonian citizens
      if (politicalMember.citizenship?.toLowerCase() === 'ee') {
        if (!(politicalMember.personalCode || '').trim()) {
          newErrors.politicalPersonalCode = t('kyc.step5.validation.required');
          hasErrors = true;
        } else if (!validateEstonianPersonalCode(politicalMember.personalCode || '')) {
          newErrors.politicalPersonalCode = t('kyc.step5.validation.invalidPersonalCode');
          hasErrors = true;
        }
      }
      
      if (!politicalMember.citizenship) {
        newErrors.politicalCitizenship = t('kyc.step5.validation.required');
        hasErrors = true;
      }
    }
    
    // Validate political details for all members
    if (!(politicalMember.institution || '').trim()) {
      newErrors.politicalInstitution = t('kyc.step5.validation.required');
      hasErrors = true;
    }
    
    if (!(politicalMember.position || '').trim()) {
      newErrors.politicalPosition = t('kyc.step5.validation.required');
      hasErrors = true;
    }
    
    if (!politicalMember.relationship) {
      newErrors.politicalRelationship = t('kyc.step5.validation.required');
      hasErrors = true;
    }
    
    if (!politicalMember.birthday) {
      newErrors.politicalBirthday = t('kyc.step5.validation.birthdayRequired');
      hasErrors = true;
    }
    
    if (hasErrors) {
      setEditErrors(newErrors);
      return;
    }
    
    // Clear errors on successful confirmation
    setEditErrors({});
    
    setForm(prev => ({
      ...prev,
      politicalMembers: prev.politicalMembers.map((pm, i) => 
        i === index ? { ...pm, isConfirmed: true } : pm
      )
    }));
  };

  return (
    <div className="min-h-screen bg-[#F7F7FA] font-sans">
      {/* Header */}
      <header className="w-full bg-white flex flex-col px-10 py-5 border-b-2 border-[#00564B]">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center">
            <img src="https://greenit.ee/wp-content/themes/greenit/assets/svg/greenit-logo-green.svg" alt="GreenIT Logo" className="w-32 h-auto mr-8" />
            <span className="text-2xl font-semibold tracking-tight text-[#00564B]">{t('kyc.title')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-4 py-1 rounded-full bg-[#F7F7FA] text-[#00564B] hover:bg-[#C3ECD9] transition" onClick={() => changeLang('en')}>EN</button>
            <button className="px-4 py-1 rounded-full bg-[#F7F7FA] text-[#00564B] hover:bg-[#C3ECD9] transition" onClick={() => changeLang('et')}>ET</button>
          </div>
        </div>
        <div className="w-full mt-4">
          <div className="w-full">
            <div className="flex items-center bg-green-50 border border-green-200 text-green-900 rounded-lg px-6 py-3 text-sm">
              <svg className="w-6 h-6 mr-3 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" /></svg>
              <span>
                {t('kyc.notification')}
              </span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Content Area */}
      {isLoading ? (
        // Cool Loading Overlay with Progress Bar
        <div className="fixed inset-0 bg-white/95 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 border border-gray-100">
            {/* Logo/Icon */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[#00564B] to-[#6B897B] rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-[#00564B] mb-2">{t('kyc.loading.title')}</h2>
              <p className="text-gray-600">{t('kyc.loading.subtitle')}</p>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{t('kyc.loading.progress')}</span>
                <span className="text-[#00564B] font-semibold">75%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#00564B] to-[#6B897B] rounded-full animate-pulse relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>
            </div>

            {/* Loading Steps */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">{t('kyc.loading.steps.authenticating')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">{t('kyc.loading.steps.fetching')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#6B897B] rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">{t('kyc.loading.steps.processing')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span className="text-sm text-gray-400">{t('kyc.loading.steps.ready')}</span>
              </div>
            </div>

            {/* Animated Dots */}
            <div className="flex justify-center mt-6">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-[#00564B] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#6B897B] rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                <div className="w-2 h-2 bg-[#00564B] rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Main Content
        <div className="flex min-h-[calc(100vh-72px)]">
          {/* Sidebar */}
          <aside className="w-[320px] flex flex-col pt-12 pl-10 bg-[#F7F7FA]">
          <ol className="space-y-8">
            {steps.map((s, idx) => {
              const stepNum = idx + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <li key={s.key} className={`flex items-center group p-3 ${isActive ? 'bg-[#FDFDFD] border-r-2 border-[#FDFDFD] rounded-l-lg' : ''}`}>
                                           <div className={`flex items-center justify-center w-8 h-8 rounded-full border ${isActive ? 'border-[#00564B] bg-[#00564B] text-white' : isCompleted ? 'border-[#6B897B] bg-[#6B897B] text-white' : 'border-gray-300 bg-gray-200 text-gray-500'} transition`}>
                           {isCompleted ? (
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                           ) : stepNum}
                         </div>
                  <span className={`ml-4 text-base ${isActive ? 'text-[#00564B] font-semibold' : 'text-gray-500'} group-hover:text-[#00564B] transition`}>{t(`kyc.steps.${s.key}`)}</span>
                </li>
              );
            })}
          </ol>
        </aside>
        {/* Main Content */}
        <main className="flex-1 flex flex-col bg-[#FDFDFD]">
          <div className="w-full px-16 mx-auto my-8">
            <form className="w-[90%] space-y-10 border border-gray-200 rounded-2xl bg-white p-12 mx-auto">
              {/* Step 1: Company Data */}
              {step === 1 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step1.title')}</h2>
                  <div className="grid grid-cols-2 gap-8">
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.companyName')}</label>
                      <input 
                        name="companyName" 
                        value={form.companyName} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.registryCode')}</label>
                      <input 
                        name="registryCode" 
                        value={form.registryCode} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.telephone')}</label>
                      <input 
                        name="telephone" 
                        value={form.telephone} 
                        onChange={handleChange}
                        className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.telephone ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                      />
                      {errors.telephone && <div className="text-red-500 text-sm mt-1">{errors.telephone}</div>}
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.email')}</label>
                      <input 
                        name="email" 
                        type="email"
                        value={form.email} 
                        onChange={handleChange}
                        className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.email ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                      />
                      {errors.email && <div className="text-red-500 text-sm mt-1">{errors.email}</div>}
                    </div>
                    {/* Legal Address Section */}
                    <div className="col-span-2">
                      <h3 className="text-lg font-semibold mb-4 text-[#00564B]">{t('kyc.step1.legalAddress')}</h3>
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.legalAddress')}</label>
                      <input 
                        name="legalAddress" 
                        value={form.legalAddress} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.legalDistrict')}</label>
                      <input 
                        name="legalCity" 
                        value={form.legalCity} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.legalCounty')}</label>
                      <input 
                        name="legalCounty" 
                        value={form.legalCounty} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.legalCountry')}</label>
                      <input 
                        name="legalCountry" 
                        value={form.legalCountry} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step1.legalZipCode')}</label>
                      <input 
                        name="legalZipCode" 
                        value={form.legalZipCode} 
                        onChange={handleChange}
                        disabled
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 text-gray-400 border border-transparent focus:outline-none" 
                      />
                    </div>
                    <div className="col-span-2 flex items-center mt-2">
                      <input 
                        type="checkbox" 
                        id="differentAddress" 
                        name="differentAddress"
                        checked={form.differentAddress}
                        onChange={handleChange}
                        className="mr-2" 
                      />
                      <label htmlFor="differentAddress" className="text-gray-600">{t('kyc.step1.differentAddress')}</label>
                    </div>
                    {form.differentAddress && (
                      <>
                        {/* Business Address Section */}
                        <div className="col-span-2">
                          <h3 className="text-lg font-semibold mb-4 text-[#00564B]">{t('kyc.step1.businessAddress')}</h3>
                        </div>
                        <div>
                          <label className="block mb-2 text-gray-600">{t('kyc.step1.businessAddress')}</label>
                          <input 
                            name="businessAddress" 
                            value={form.businessAddress} 
                            onChange={handleChange}
                            className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.businessAddress ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                          />
                          {errors.businessAddress && <div className="text-red-500 text-sm mt-1">{errors.businessAddress}</div>}
                        </div>
                        <div>
                          <label className="block mb-2 text-gray-600">{t('kyc.step1.businessDistrict')}</label>
                          <input 
                            name="businessCity" 
                            value={form.businessCity} 
                            onChange={handleChange}
                            className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.businessCity ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                          />
                          {errors.businessCity && <div className="text-red-500 text-sm mt-1">{errors.businessCity}</div>}
                        </div>
                        <div>
                          <label className="block mb-2 text-gray-600">{t('kyc.step1.businessCounty')}</label>
                          <input 
                            name="businessCounty" 
                            value={form.businessCounty} 
                            onChange={handleChange}
                            className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.businessCounty ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                          />
                          {errors.businessCounty && <div className="text-red-500 text-sm mt-1">{errors.businessCounty}</div>}
                        </div>
                        <div>
                          <label className="block mb-2 text-gray-600">{t('kyc.step1.businessCountry')}</label>
                          <input 
                            name="businessCountry" 
                            value={form.businessCountry} 
                            onChange={handleChange}
                            className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.businessCountry ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                          />
                          {errors.businessCountry && <div className="text-red-500 text-sm mt-1">{errors.businessCountry}</div>}
                        </div>
                        <div>
                          <label className="block mb-2 text-gray-600">{t('kyc.step1.businessZipCode')}</label>
                          <input 
                            name="businessZipCode" 
                            value={form.businessZipCode} 
                            onChange={handleChange}
                            className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.businessZipCode ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`} 
                          />
                          {errors.businessZipCode && <div className="text-red-500 text-sm mt-1">{errors.businessZipCode}</div>}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-end">
                      <button type="button" className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition" onClick={handleNext}>{t('kyc.step1.next')} ›</button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 2: Business Activity */}
              {step === 2 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step2.title')}</h2>
                  <div className="space-y-8">
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step2.activity')}</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={form.activityText || ''}
                          onChange={(e) => {
                            setForm(prev => ({ 
                              ...prev, 
                              activityText: e.target.value,
                              activity: '' // Clear the selected activity when manually typing
                            }));
                          }}
                          placeholder="Vali tegevusala või kirjuta oma..."
                          className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 pr-12 border ${errors.activity ? 'border-red-500' : form.activityText ? 'border-[#6B897B]' : 'border-transparent'} focus:outline-none focus:border-[#00564B] hover:border-[#6B897B] transition-colors ${form.activityText ? 'text-gray-900' : 'text-gray-500'}`}
                        />
                        {form.activityText && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setForm(prev => ({ ...prev, activity: '', activityText: '' }));
                              }}
                              className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition"
                              title="Clear selection"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            {!form.activity && (
                              <span className="absolute right-16 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                Custom
                              </span>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowActivityModal(true)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#00564B] transition"
                          title="Search activities"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </button>
                      </div>
                      {errors.activity && <div className="text-red-500 text-sm mt-1">{errors.activity}</div>}
                    </div>
                    
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step2.activityDescription')}</label>
                      <textarea 
                        name="activityDescription" 
                        value={form.activityDescription} 
                        onChange={handleChange}
                        rows={4}
                        className="w-full bg-[#F7F7FA] rounded-lg px-4 py-3 border border-transparent focus:outline-none focus:border-[#00564B]"
                        placeholder="Kirjeldage oma tegevust..."
                      />
                    </div>
                    
                                                            <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step2.activityAreas')}</label>
                      <Select
                        options={countryOptions}
                        value={countryOptions.filter(option => form.activityAreas?.includes(option.value))}
                        onChange={handleCountriesChange}
                        placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                        isMulti
                        isSearchable
                        menuPortalTarget={document.body}
                        styles={{
                          ...selectStyles,
                          control: (provided: any, state: any) => ({
                            ...selectStyles.control(provided, state),
                            border: errors.activityAreas ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                          })
                        }}
                      />
                      {errors.activityAreas && <div className="text-red-500 text-sm mt-1">{errors.activityAreas}</div>}
                    </div>
                    
                    <div>
                      <label className="block mb-2 text-gray-600">{t('kyc.step2.mainPartners')}</label>
                      <textarea 
                        name="mainPartners" 
                        value={form.mainPartners} 
                        onChange={handleChange}
                        rows={4}
                        className={`w-full bg-[#F7F7FA] rounded-lg px-4 py-3 ${errors.mainPartners ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#00564B]`}
                        placeholder="Sisesta peamised koostööpartnerid ja kliendid..."
                      />
                      {errors.mainPartners && <div className="text-red-500 text-sm mt-1">{errors.mainPartners}</div>}
                    </div>
                  </div>
                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-between">
                      <button type="button" className="px-8 py-3 rounded-full bg-gray-200 text-[#00564B] font-semibold hover:bg-[#C3ECD9] transition" onClick={handleBack}>{t('kyc.step2.back')}</button>
                      <button type="button" className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition" onClick={handleNext}>{t('kyc.step2.next')} ›</button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Activity Selection Modal */}
              {showActivityModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200">
                  <div className="activity-modal bg-white rounded-xl p-6 w-full max-w-2xl mx-4 shadow-2xl animate-in zoom-in-95 duration-200">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.step2.activity')}</h3>
                      <button
                        type="button"
                        onClick={() => setShowActivityModal(false)}
                        className="text-gray-400 hover:text-gray-600 transition"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      {form.activityText && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600 mb-1">Current selection:</p>
                          <p className="text-gray-900 font-medium">{form.activityText}</p>
                        </div>
                      )}
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <Select
                          options={activityOptions}
                          value={activityOptions.find(option => option.value === form.activity)}
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              handleActivityChange(selectedOption);
                              setShowActivityModal(false);
                            }
                          }}
                          placeholder="Otsi tegevusala..."
                          isSearchable
                          isClearable
                          menuPortalTarget={document.body}
                          noOptionsMessage={() => t('kyc.step2.noActivityFound')}
                          loadingMessage={() => "Otsin..."}
                          filterOption={(option, inputValue) => {
                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                          }}
                          styles={{
                            ...selectStyles,
                            control: (provided: any, state: any) => ({
                              ...selectStyles.control(provided, state),
                              border: state.isFocused ? '1px solid #6B897B' : '1px solid #E5E7EB',
                              paddingLeft: '2.5rem'
                            })
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowActivityModal(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors"
                      >
                        {t('kyc.step3.cancel')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Step 3: Board Members */}
              {step === 3 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step3.title')}</h2>
                  <p className="text-gray-600 mb-6">{t('kyc.step3.description')}</p>
                  
                  <div className="space-y-6">
                    {/* Add Person Button */}
                    {!isAddingMember && editingMember === null && (
                      <button
                        type="button"
                        onClick={handleAddMember}
                        className="px-6 py-3 border border-[#6B897B] text-[#6B897B] rounded-lg hover:bg-[#6B897B] hover:text-white transition flex items-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('kyc.step3.addPerson')}
                      </button>
                    )}

                    {/* Add New Member Form */}
                    {isAddingMember && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-[#00564B]">
                                {t('kyc.step3.addPerson')}
                              </h3>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.firstname')} *
                            </label>
                            <input
                              type="text"
                              name="firstname"
                              value={editForm.firstname}
                              onChange={handleEditFormChange}
                              className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editFirstname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                            />
                            {editErrors.editFirstname && <div className="text-red-500 text-sm mt-1">{editErrors.editFirstname}</div>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.lastname')} *
                            </label>
                            <input
                              type="text"
                              name="lastname"
                              value={editForm.lastname}
                              onChange={handleEditFormChange}
                              className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editLastname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                            />
                            {editErrors.editLastname && <div className="text-red-500 text-sm mt-1">{editErrors.editLastname}</div>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.personalCode')}
                            </label>
                            <input
                              type="text"
                              name="personalCode"
                              value={editForm.personalCode}
                              onChange={handleEditFormChange}
                              className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editPersonalCode ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                            />
                            {editErrors.editPersonalCode && <div className="text-red-500 text-sm mt-1">{editErrors.editPersonalCode}</div>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.citizenship')} *
                            </label>
                            <Select
                              options={countryOptions}
                              value={countryOptions.find(option => option.value === editForm.citizenship)}
                              onChange={handleCitizenshipChange}
                              placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                              isSearchable
                              menuPortalTarget={document.body}
                              styles={{
                                ...selectStyles,
                                control: (provided: any, state: any) => ({
                                  ...selectStyles.control(provided, state),
                                  border: editErrors.editCitizenship ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                                })
                              }}
                            />
                            {editErrors.editCitizenship && <div className="text-red-500 text-sm mt-1">{editErrors.editCitizenship}</div>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.birthday')} *
                            </label>
                            <DatePicker
                              selected={editForm.birthday}
                              onChange={handleDateChange}
                              dateFormat="dd.MM.yyyy"
                              locale={i18n.language === 'et' ? 'et' : 'en'}
                              placeholderText="DD.MM.YYYY"
                              className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 ${editErrors.editBirthday ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              maxDate={new Date()}
                            />
                            {editErrors.editBirthday && <div className="text-red-500 text-sm mt-1">{editErrors.editBirthday}</div>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.experience')}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  {t('kyc.step3.years')}
                                </label>
                                <input
                                  type="number"
                                  value={editExperienceYears}
                                  onChange={handleExperienceYearsChange}
                                  min="0"
                                  className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editExperience ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                  {t('kyc.step3.months')}
                                </label>
                                <input
                                  type="number"
                                  value={editExperienceMonths}
                                  onChange={handleExperienceMonthsChange}
                                  min="0"
                                  max="11"
                                  className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editExperience ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                />
                              </div>
                            </div>
                            {editErrors.editExperience && <div className="text-red-500 text-sm mt-1">{editErrors.editExperience}</div>}
                          </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                          <button
                            type="button"
                            onClick={handleSaveAdd}
                            className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition"
                          >
                            {t('kyc.step3.save')}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelAdd}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                          >
                            {t('kyc.step3.cancel')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Board Members List */}
                    {form.boardMembers.map((member, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                        {editingMember === index ? (
                          // Edit Mode
                          <div>
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-[#00564B]">
                                    {t('kyc.step3.edit')} {t('kyc.step3.title').toLowerCase()}
                                  </h3>
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('kyc.step3.firstname')} *
                                </label>
                                <input
                                  type="text"
                                  name="firstname"
                                  value={editForm.firstname}
                                  onChange={handleEditFormChange}
                                  className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('kyc.step3.lastname')} *
                                </label>
                                <input
                                  type="text"
                                  name="lastname"
                                  value={editForm.lastname}
                                  onChange={handleEditFormChange}
                                  className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('kyc.step3.personalCode')}
                                </label>
                                <input
                                  type="text"
                                  name="personalCode"
                                  value={editForm.personalCode}
                                  onChange={handleEditFormChange}
                                  className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                />
                              </div>
                                                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.citizenship')}
                            </label>
                            <Select
                              options={countryOptions}
                              value={countryOptions.find(option => option.value === editForm.citizenship)}
                              onChange={handleCitizenshipChange}
                              placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                              isSearchable
                              menuPortalTarget={document.body}
                              styles={selectStyles}
                            />
                          </div>
                                                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('kyc.step3.birthday')}
                            </label>
                            <DatePicker
                              selected={editForm.birthday}
                              onChange={handleDateChange}
                              dateFormat="dd.MM.yyyy"
                              locale={i18n.language === 'et' ? 'et' : 'en'}
                              placeholderText="DD.MM.YYYY"
                              className="w-full bg-[#F7F7FA] rounded-lg px-3 py-3 border border-transparent focus:outline-none focus:border-[#6B897B]"
                              showYearDropdown
                              scrollableYearDropdown
                              yearDropdownItemNumber={100}
                              maxDate={new Date()}
                            />
                          </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('kyc.step3.experience')}
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                      {t('kyc.step3.years')}
                                    </label>
                                    <input
                                      type="number"
                                      value={editExperienceYears}
                                      onChange={handleExperienceYearsChange}
                                      min="0"
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">
                                      {t('kyc.step3.months')}
                                    </label>
                                    <input
                                      type="number"
                                      value={editExperienceMonths}
                                      onChange={handleExperienceMonthsChange}
                                      min="0"
                                      max="11"
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 flex gap-3">
                              <button
                                type="button"
                                onClick={handleSaveEdit}
                                className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition"
                              >
                                {t('kyc.step3.save')}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                              >
                                {t('kyc.step3.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-[#00564B]">
                                    {member.firstname} {member.lastname}
                                  </h3>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleEditMember(index)}
                                className="text-[#6B897B] hover:text-[#5A7A6A] transition flex items-center gap-1 text-sm"
                              >
                                <span>{t('kyc.step3.edit')}</span>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </button>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                <span className="ml-2 font-medium">{member.personalCode}</span>
                              </div>
                                                        <div>
                            <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                            <span className="ml-2 font-medium">
                              {countries.find(c => c.alpha2 === member.citizenship)?.name || member.citizenship}
                            </span>
                          </div>
                                                        <div>
                            <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                            <span className="ml-2 font-medium">
                              {member.birthday ? member.birthday.toLocaleDateString('et-EE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }) : '-'}
                            </span>
                          </div>
                              <div>
                                <span className="text-gray-500">{t('kyc.step3.experience')}:</span>
                                <span className="ml-2 font-medium">
                                  {Math.floor(member.experience / 12)}a {member.experience % 12}k
                                </span>
                              </div>
                            </div>

                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <button
                                type="button"
                                onClick={() => handleDeleteMember(index)}
                                className="text-red-500 hover:text-red-700 transition flex items-center gap-1 text-sm"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                {t('kyc.step3.delete')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {form.boardMembers.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        {t('kyc.step3.noMembers')}
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-between">
                      <button type="button" className="px-8 py-3 rounded-full bg-gray-200 text-[#00564B] font-semibold hover:bg-[#C3ECD9] transition" onClick={handleBack}>‹ {t('kyc.step3.back')}</button>
                      <button type="button" className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition" onClick={handleNext}>{t('kyc.step3.next')} ›</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Beneficial Owners */}
              {step === 4 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step4.title')}</h2>
                  
                  {/* Definition */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                    <p className="text-gray-900 mb-4">
                      {showExtendedDefinition ? t('kyc.step4.definitionExtended') : t('kyc.step4.definition')}
                    </p>
                    <button 
                      type="button"
                      onClick={() => setShowExtendedDefinition(!showExtendedDefinition)}
                      className="text-gray-600 hover:text-gray-800 transition flex items-center gap-1 text-sm"
                    >
                      {showExtendedDefinition ? t('kyc.step4.showLess') : t('kyc.step4.showMore')}
                      <svg 
                        className={`w-4 h-4 transition-transform ${showExtendedDefinition ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Exemptions */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                    <h3 className="font-semibold text-gray-800 mb-4">{t('kyc.step4.exemptionsTitle')}</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                      <li>{t('kyc.step4.exemption1')}</li>
                      <li>{t('kyc.step4.exemption2')}</li>
                      <li>{t('kyc.step4.exemption3')}</li>
                      <li>{t('kyc.step4.exemption4')}</li>
                      <li>{t('kyc.step4.exemption5')}</li>
                    </ul>
                    <p className="text-gray-600 mt-4 text-sm">
                      {t('kyc.step4.exemptionInstruction')}
                    </p>
                  </div>

                  {/* Declaration */}
                  <div className="mb-8">
                    <h3 className="font-semibold text-gray-800 mb-4">{t('kyc.step4.declarationTitle')}</h3>
                    <p className="text-gray-600 mb-4">{t('kyc.step4.declarationInstruction')}</p>
                    
                    <div className="flex gap-4 mb-6">
                      <button
                        type="button"
                        onClick={() => handleBOObligatoryChange(false)}
                        className={`px-6 py-3 rounded-lg border transition ${
                          !form.beneficialOwnersObligatory 
                            ? 'bg-[#6B897B] text-white border-[#6B897B]' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#6B897B]'
                        }`}
                      >
                        {t('kyc.step4.notObligatory')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBOObligatoryChange(true)}
                        className={`px-6 py-3 rounded-lg border transition ${
                          form.beneficialOwnersObligatory 
                            ? 'bg-[#6B897B] text-white border-[#6B897B]' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#6B897B]'
                        }`}
                      >
                        {t('kyc.step4.obligatory')}
                      </button>
                    </div>

                    {form.beneficialOwnersObligatory && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 text-green-800">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                          </svg>
                          <span className="text-sm">{t('kyc.step4.checkMessage')}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Conditional Content */}
                  {!form.beneficialOwnersObligatory ? (
                    // Not obligatory - show reason dropdown
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('kyc.step4.reasonLabel')} *
                        </label>
                        <Select
                          options={exemptionReasonOptions}
                          value={exemptionReasonOptions.find(option => option.value === form.reasonOfNoBO)}
                          onChange={(selectedOption) => {
                            setForm(prev => ({
                              ...prev,
                              reasonOfNoBO: selectedOption ? selectedOption.value : ''
                            }));
                            // Clear error when user selects
                            if (errors.reasonOfNoBO) {
                              setErrors(prev => ({ ...prev, reasonOfNoBO: undefined }));
                            }
                          }}
                          placeholder={t('kyc.step4.reasonPlaceholder')}
                          isSearchable
                          menuPortalTarget={document.body}
                          styles={{
                            ...selectStyles,
                            control: (provided: any, state: any) => ({
                              ...selectStyles.control(provided, state),
                              border: errors.reasonOfNoBO ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                            }),
                            menuPortal: (provided: any) => ({
                              ...provided,
                              zIndex: 9999
                            })
                          }}
                        />
                        {errors.reasonOfNoBO && <div className="text-red-500 text-sm mt-1">{errors.reasonOfNoBO}</div>}
                      </div>
                    </div>
                  ) : (
                    // Obligatory - show beneficial owners list
                    <div className="space-y-6">
                      {/* Add Person Button */}
                      {!isAddingBO && editingBO === null && (
                        <button
                          type="button"
                          onClick={handleAddBO}
                          className="px-6 py-3 border border-[#6B897B] text-[#6B897B] rounded-lg hover:bg-[#6B897B] hover:text-white transition flex items-center gap-2"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {t('kyc.step4.addPerson')}
                        </button>
                      )}

                      {/* Add New Beneficial Owner Form */}
                      {isAddingBO && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-[#00564B]">
                                  {t('kyc.step4.addPerson')}
                                </h3>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('kyc.step3.firstname')} *
                              </label>
                              <input
                                type="text"
                                name="firstname"
                                value={editBOForm.firstname}
                                onChange={handleEditBOFormChange}
                                className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOFirstname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                              />
                              {editErrors.editBOFirstname && <div className="text-red-500 text-sm mt-1">{editErrors.editBOFirstname}</div>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('kyc.step3.lastname')} *
                              </label>
                              <input
                                type="text"
                                name="lastname"
                                value={editBOForm.lastname}
                                onChange={handleEditBOFormChange}
                                className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOLastname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                              />
                              {editErrors.editBOLastname && <div className="text-red-500 text-sm mt-1">{editErrors.editBOLastname}</div>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('kyc.step3.personalCode')}
                              </label>
                              <input
                                type="text"
                                name="personalCode"
                                value={editBOForm.personalCode}
                                onChange={handleEditBOFormChange}
                                className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOPersonalCode ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                              />
                              {editErrors.editBOPersonalCode && <div className="text-red-500 text-sm mt-1">{editErrors.editBOPersonalCode}</div>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('kyc.step3.citizenship')} *
                              </label>
                              <Select
                                options={countryOptions}
                                value={countryOptions.find(option => option.value === editBOForm.citizenship)}
                                onChange={handleBOCitizenshipChange}
                                placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                                isSearchable
                                menuPortalTarget={document.body}
                                styles={{
                                  ...selectStyles,
                                  control: (provided: any, state: any) => ({
                                    ...selectStyles.control(provided, state),
                                    border: editErrors.editBOCitizenship ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                                  })
                                }}
                              />
                              {editErrors.editBOCitizenship && <div className="text-red-500 text-sm mt-1">{editErrors.editBOCitizenship}</div>}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('kyc.step3.birthday')} *
                              </label>
                              <DatePicker
                                selected={editBOForm.birthday}
                                onChange={handleBODateChange}
                                dateFormat="dd.MM.yyyy"
                                locale={i18n.language === 'et' ? 'et' : 'en'}
                                placeholderText="DD.MM.YYYY"
                                className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-3 ${editErrors.editBOBirthday ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                maxDate={new Date()}
                              />
                              {editErrors.editBOBirthday && <div className="text-red-500 text-sm mt-1">{editErrors.editBOBirthday}</div>}
                            </div>

                          </div>

                          <div className="mt-6 flex gap-3">
                            <button
                              type="button"
                              onClick={handleSaveAddBO}
                              className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition"
                            >
                              {t('kyc.step3.save')}
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelAddBO}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                            >
                              {t('kyc.step3.cancel')}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Beneficial Owners List */}
                      {form.beneficialOwners.map((bo, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                          {editingBO === index ? (
                            // Edit Mode
                            <div>
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-semibold text-[#00564B]">
                                      {t('kyc.step4.edit')} {t('kyc.step4.title').toLowerCase()}
                                    </h3>
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('kyc.step3.firstname')} *
                                  </label>
                                  <input
                                    type="text"
                                    name="firstname"
                                    value={editBOForm.firstname}
                                    onChange={handleEditBOFormChange}
                                    className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOFirstname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                  />
                                  {editErrors.editBOFirstname && <div className="text-red-500 text-sm mt-1">{editErrors.editBOFirstname}</div>}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('kyc.step3.lastname')} *
                                  </label>
                                  <input
                                    type="text"
                                    name="lastname"
                                    value={editBOForm.lastname}
                                    onChange={handleEditBOFormChange}
                                    className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOLastname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                  />
                                  {editErrors.editBOLastname && <div className="text-red-500 text-sm mt-1">{editErrors.editBOLastname}</div>}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('kyc.step3.personalCode')}
                                  </label>
                                  <input
                                    type="text"
                                    name="personalCode"
                                    value={editBOForm.personalCode}
                                    onChange={handleEditBOFormChange}
                                    className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.editBOPersonalCode ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                  />
                                  {editErrors.editBOPersonalCode && <div className="text-red-500 text-sm mt-1">{editErrors.editBOPersonalCode}</div>}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('kyc.step3.citizenship')} *
                                  </label>
                                  <Select
                                    options={countryOptions}
                                    value={countryOptions.find(option => option.value === editBOForm.citizenship)}
                                    onChange={handleBOCitizenshipChange}
                                    placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    styles={{
                                      ...selectStyles,
                                      control: (provided: any, state: any) => ({
                                        ...selectStyles.control(provided, state),
                                        border: editErrors.editBOCitizenship ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                                      })
                                    }}
                                  />
                                  {editErrors.editBOCitizenship && <div className="text-red-500 text-sm mt-1">{editErrors.editBOCitizenship}</div>}
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('kyc.step3.birthday')} *
                                  </label>
                                  <DatePicker
                                    selected={editBOForm.birthday}
                                    onChange={handleBODateChange}
                                    dateFormat="dd.MM.yyyy"
                                    locale={i18n.language === 'et' ? 'et' : 'en'}
                                    placeholderText="DD.MM.YYYY"
                                    className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-3 ${editErrors.editBOBirthday ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    maxDate={new Date()}
                                  />
                                  {editErrors.editBOBirthday && <div className="text-red-500 text-sm mt-1">{editErrors.editBOBirthday}</div>}
                                </div>

                              </div>

                              <div className="mt-6 flex gap-3">
                                <button
                                  type="button"
                                  onClick={handleSaveEditBO}
                                  className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition"
                                >
                                  {t('kyc.step3.save')}
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelEditBO}
                                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                >
                                  {t('kyc.step3.cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div>
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-[#6B897B] rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-semibold text-[#00564B]">
                                      {bo.firstname} {bo.lastname}
                                    </h3>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleEditBO(index)}
                                  className="text-[#6B897B] hover:text-[#5A7A6A] transition flex items-center gap-1 text-sm"
                                >
                                  <span>{t('kyc.step4.edit')}</span>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </button>
                              </div>

                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                  <span className="ml-2 font-medium">{bo.personalCode}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                                  <span className="ml-2 font-medium">
                                    {countries.find(c => c.alpha2 === bo.citizenship)?.name || bo.citizenship}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                                  <span className="ml-2 font-medium">
                                    {bo.birthday ? bo.birthday.toLocaleDateString('et-EE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    }) : '-'}
                                  </span>
                                </div>

                              </div>

                              <div className="mt-4 pt-4 border-t border-gray-100">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBO(index)}
                                  className="text-red-500 hover:text-red-700 transition flex items-center gap-1 text-sm"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  {t('kyc.step4.delete')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {form.beneficialOwners.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          {t('kyc.step4.noOwners')}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-between">
                      <button type="button" className="px-8 py-3 rounded-full bg-gray-200 text-[#00564B] font-semibold hover:bg-[#C3ECD9] transition" onClick={handleBack}>‹ {t('kyc.step4.back')}</button>
                      <button type="button" className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition" onClick={handleNext}>{t('kyc.step4.next')} ›</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Politically Exposed Person */}
              {step === 5 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step5.title')}</h2>
                  
                  {/* Definition */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                    <p className="text-gray-900 mb-4">
                      {showExtendedDefinitionStep5 ? t('kyc.step5.definitionExtended') : t('kyc.step5.definition')}
                    </p>
                    <button 
                      type="button"
                      onClick={() => setShowExtendedDefinitionStep5(!showExtendedDefinitionStep5)}
                      className="text-gray-600 hover:text-gray-800 transition flex items-center gap-1 text-sm"
                    >
                      {showExtendedDefinitionStep5 ? t('kyc.step5.showLess') : t('kyc.step5.showMore')}
                      <svg 
                        className={`w-4 h-4 transition-transform ${showExtendedDefinitionStep5 ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Instruction */}
                  <div className="mb-8">
                    <p className="text-gray-700 mb-6">
                      {t('kyc.step5.instruction')}
                    </p>
                    
                    <div className="flex gap-4 mb-8">
                      <button
                        type="button"
                        onClick={() => handlePoliticalMembersChange(false)}
                        className={`px-6 py-3 rounded-lg border transition ${
                          !form.hasPoliticalMembers 
                            ? 'bg-[#6B897B] text-white border-[#6B897B]' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#6B897B]'
                        }`}
                      >
                        {t('kyc.step5.noPoliticalMembers')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePoliticalMembersChange(true)}
                        className={`px-6 py-3 rounded-lg border transition ${
                          form.hasPoliticalMembers 
                            ? 'bg-[#6B897B] text-white border-[#6B897B]' 
                            : 'bg-white text-gray-700 border-gray-300 hover:border-[#6B897B]'
                        }`}
                      >
                        {t('kyc.step5.hasPoliticalMembers')}
                      </button>
                    </div>
                  </div>

                  {/* Conditional Content */}
                  {form.hasPoliticalMembers && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-end gap-3 mb-4">
                        {form.beneficialOwners.filter(bo => !form.politicalMembers.some(pm => pm.personalCode === bo.personalCode)).length > 0 && (
                          <button
                            type="button"
                            onClick={handleOpenPersonSelectionModal}
                            className="px-4 py-2 border border-[#6B897B] text-[#6B897B] rounded-lg hover:bg-[#6B897B] hover:text-white transition flex items-center gap-2 text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {t('kyc.step5.selectPersonsShort')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleAddNewPoliticalMember}
                          className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition flex items-center gap-2 text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {t('kyc.step5.addNewPersonShort')}
                        </button>
                      </div>
                      

                      {/* Politically Exposed Person Details */}
                      {form.politicalMembers.length > 0 && (
                        <div className="mt-8">
                          <h3 className="text-lg font-semibold text-gray-800 mb-4">
                            {t('kyc.step5.politicalDetails')}
                          </h3>
                          
                                                     <div className="space-y-6">
                             {form.politicalMembers.map((pm, index) => (
                               <div key={index} className={`bg-white border rounded-lg p-6 shadow-sm ${
                                 pm.isConfirmed ? 'border-green-200 bg-green-50' : 'border-gray-200'
                               } ${editingPoliticalMember === index ? 'hidden' : ''}`}>
                                 <div className="flex items-center gap-3 mb-4">
                                   <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                     pm.isConfirmed ? 'bg-green-500' : 'bg-[#6B897B]'
                                   }`}>
                                     {pm.isConfirmed ? (
                                       <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                       </svg>
                                     ) : (
                                       <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                       </svg>
                                     )}
                                   </div>
                                   <div className="flex-1">
                                     <h4 className="text-lg font-semibold text-[#00564B]">
                                       {pm.firstname} {pm.lastname}
                                     </h4>
                                     {pm.isConfirmed && (
                                       <p className="text-sm text-green-600 mt-1">
                                         {t('kyc.step5.confirmed')}
                                       </p>
                                     )}
                                   </div>
                                   <div className="flex items-center gap-2">
                                     <button
                                       type="button"
                                       onClick={() => handleEditPoliticalMember(index)}
                                       className="px-3 py-1 text-[#6B897B] hover:text-[#5A7A6A] hover:bg-gray-50 rounded-lg transition flex items-center gap-1 text-sm"
                                     >
                                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                       </svg>
                                       {t('kyc.step5.edit')}
                                     </button>
                                     <button
                                       type="button"
                                       onClick={() => handleRemovePoliticalMember(index)}
                                       className="px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition flex items-center gap-1 text-sm"
                                     >
                                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                       </svg>
                                       {t('kyc.step5.remove')}
                                     </button>
                                   </div>
                                 </div>

                                 {!pm.isConfirmed ? (
                                   <>
                                     {/* Personal Information Fields for New Persons */}
                                                                            {!pm.isConfirmed && (
                                       <div className="mb-6">
                                         <h4 className="text-md font-medium text-gray-800 mb-4">
                                           {t('kyc.step5.personalInformation')}
                                         </h4>
                                         <div className="grid grid-cols-2 gap-4">
                                           <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">
                                               {t('kyc.step3.firstname')} *
                                             </label>
                                             <input
                                               type="text"
                                               value={pm.firstname}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'firstname', e.target.value)}
                                               className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalFirstname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                               placeholder={t('kyc.step3.firstnamePlaceholder')}
                                             />
                                             {editErrors.politicalFirstname && <div className="text-red-500 text-sm mt-1">{editErrors.politicalFirstname}</div>}
                                           </div>
                                           <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">
                                               {t('kyc.step3.lastname')} *
                                             </label>
                                             <input
                                               type="text"
                                               value={pm.lastname}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'lastname', e.target.value)}
                                               className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalLastname ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                               placeholder={t('kyc.step3.lastnamePlaceholder')}
                                             />
                                             {editErrors.politicalLastname && <div className="text-red-500 text-sm mt-1">{editErrors.politicalLastname}</div>}
                                           </div>
                                           <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">
                                               {t('kyc.step3.personalCode')}
                                             </label>
                                             <input
                                               type="text"
                                               value={pm.personalCode}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'personalCode', e.target.value)}
                                               className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalPersonalCode ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                               placeholder={t('kyc.step3.personalCodePlaceholder')}
                                             />
                                             {editErrors.politicalPersonalCode && <div className="text-red-500 text-sm mt-1">{editErrors.politicalPersonalCode}</div>}
                                           </div>
                                           <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">
                                               {t('kyc.step3.citizenship')} *
                                             </label>
                                             <Select
                                               options={countryOptions}
                                               value={countryOptions.find(option => option.value === pm.citizenship)}
                                               onChange={(selectedOption) => handlePoliticalMemberChange(index, 'citizenship', selectedOption ? selectedOption.value : '')}
                                               placeholder={t('kyc.step3.citizenshipPlaceholder')}
                                               isSearchable
                                               menuPortalTarget={document.body}
                                               styles={{
                                                 ...selectStyles,
                                                 control: (provided: any, state: any) => ({
                                                   ...selectStyles.control(provided, state),
                                                   border: editErrors.politicalCitizenship ? '1px solid #EF4444' : state.isFocused ? '1px solid #6B897B' : '1px solid transparent'
                                                 })
                                               }}
                                             />
                                             {editErrors.politicalCitizenship && <div className="text-red-500 text-sm mt-1">{editErrors.politicalCitizenship}</div>}
                                           </div>
                                           <div>
                                             <label className="block text-sm font-medium text-gray-700 mb-1">
                                               {t('kyc.step3.birthday')} *
                                             </label>
                                             <DatePicker
                                               selected={pm.birthday}
                                               onChange={(date) => handlePoliticalMemberChange(index, 'birthday', date)}
                                               dateFormat="dd.MM.yyyy"
                                               locale={i18n.language === 'et' ? et as any : en as any}
                                               placeholderText={t('kyc.step5.birthdayPlaceholder')}
                                               className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalBirthday ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                               wrapperClassName="w-full"
                                               customInput={
                                                 <input
                                                   className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalBirthday ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                                 />
                                               }
                                             />
                                             {editErrors.politicalBirthday && <div className="text-red-500 text-sm mt-1">{editErrors.politicalBirthday}</div>}
                                           </div>
                                         </div>
                                       </div>
                                     )}

                                     {/* Political Exposure Details */}
                                     <div className="space-y-4">
                                       {/* First row: Institution and Position */}
                                       <div className="grid grid-cols-2 gap-4">
                                         <div>
                                           <label className="block text-sm font-medium text-gray-700 mb-1">
                                             {t('kyc.step5.institution')} *
                                           </label>
                                           <input
                                             type="text"
                                             value={pm.institution || ''}
                                             onChange={(e) => handlePoliticalMemberChange(index, 'institution', e.target.value)}
                                             className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalInstitution ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                             placeholder={t('kyc.step5.institutionPlaceholder')}
                                           />
                                           {editErrors.politicalInstitution && <div className="text-red-500 text-sm mt-1">{editErrors.politicalInstitution}</div>}
                                         </div>
                                         <div>
                                           <label className="block text-sm font-medium text-gray-700 mb-1">
                                             {t('kyc.step5.position')} *
                                           </label>
                                           <input
                                             type="text"
                                             value={pm.position || ''}
                                             onChange={(e) => handlePoliticalMemberChange(index, 'position', e.target.value)}
                                             className={`w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 ${editErrors.politicalPosition ? 'border border-red-500' : 'border border-transparent'} focus:outline-none focus:border-[#6B897B]`}
                                             placeholder={t('kyc.step5.positionPlaceholder')}
                                           />
                                           {editErrors.politicalPosition && <div className="text-red-500 text-sm mt-1">{editErrors.politicalPosition}</div>}
                                         </div>
                                       </div>
                                       
                                       {/* Second row: Relationship */}
                                       <div className="col-span-2">
                                         <label className="block text-sm font-medium text-gray-700 mb-1">
                                           {t('kyc.step5.relationship')} *
                                         </label>
                                         <div className="space-y-2">
                                           <label className="flex items-center">
                                             <input
                                               type="radio"
                                               name={`relationship_${index}`}
                                               value="beneficial_owner"
                                               checked={pm.relationship === 'beneficial_owner'}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'relationship', e.target.value)}
                                               className="mr-2"
                                             />
                                             <span className="text-sm">{t('kyc.step5.relationshipOption1')}</span>
                                           </label>
                                           <label className="flex items-center">
                                             <input
                                               type="radio"
                                               name={`relationship_${index}`}
                                               value="family_member"
                                               checked={pm.relationship === 'family_member'}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'relationship', e.target.value)}
                                               className="mr-2"
                                             />
                                             <span className="text-sm">{t('kyc.step5.relationshipOption2')}</span>
                                           </label>
                                           <label className="flex items-center">
                                             <input
                                               type="radio"
                                               name={`relationship_${index}`}
                                               value="close_associate"
                                               checked={pm.relationship === 'close_associate'}
                                               onChange={(e) => handlePoliticalMemberChange(index, 'relationship', e.target.value)}
                                               className="mr-2"
                                             />
                                             <span className="text-sm">{t('kyc.step5.relationshipOption3')}</span>
                                           </label>
                                         </div>
                                         {editErrors.politicalRelationship && <div className="text-red-500 text-sm mt-1">{editErrors.politicalRelationship}</div>}
                                       </div>

                                     </div>

                                     <div className="mt-6 flex gap-3">
                                       <button
                                         type="button"
                                         onClick={() => handleConfirmPoliticalMember(index)}
                                         disabled={!(pm.institution || '').trim() || !(pm.position || '').trim() || !pm.relationship}
                                         className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                                       >
                                         {t('kyc.step5.confirm')}
                                       </button>
                                       {!pm.isConfirmed && (
                                         <button
                                           type="button"
                                           onClick={() => handleRemovePoliticalMember(index)}
                                           className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                                         >
                                           {t('kyc.step5.cancel')}
                                         </button>
                                       )}
                                     </div>
                                   </>
                                 ) : (
                                   <div className="grid grid-cols-2 gap-4 text-sm">
                                     {/* Personal Information */}
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step3.firstname')}:</span>
                                       <span className="ml-2 font-medium">{pm.firstname || '-'}</span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step3.lastname')}:</span>
                                       <span className="ml-2 font-medium">{pm.lastname || '-'}</span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                       <span className="ml-2 font-medium">{pm.personalCode || '-'}</span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                                       <span className="ml-2 font-medium">
                                         {countries.find(c => c.alpha2 === pm.citizenship)?.name || pm.citizenship || '-'}
                                       </span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                                       <span className="ml-2 font-medium">{pm.birthday ? pm.birthday.toLocaleDateString('et-EE') : '-'}</span>
                                     </div>
                                     
                                     {/* Political Details */}
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step5.institution')}:</span>
                                       <span className="ml-2 font-medium">{pm.institution || '-'}</span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step5.position')}:</span>
                                       <span className="ml-2 font-medium">{pm.position || '-'}</span>
                                     </div>
                                     <div>
                                       <span className="text-gray-500">{t('kyc.step5.relationship')}:</span>
                                       <span className="ml-2 font-medium">
                                         {pm.relationship === 'beneficial_owner' ? t('kyc.step5.relationshipOption1') :
                                          pm.relationship === 'family_member' ? t('kyc.step5.relationshipOption2') :
                                          pm.relationship === 'close_associate' ? t('kyc.step5.relationshipOption3') : (pm.relationship || '-')}
                                       </span>
                                     </div>

                                   </div>
                                 )}
                               </div>
                             ))}
                           </div>
                        </div>
                      )}

                      {/* Edit Political Member Form */}
                      {editingPoliticalMember !== null && (
                        <div className="mt-8">
                          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            <h4 className="text-lg font-semibold text-gray-800 mb-6">
                              {t('kyc.step5.edit')} - {editPoliticalForm.firstname} {editPoliticalForm.lastname}
                            </h4>
                            
                            <div className="space-y-6">
                              {/* Personal Information */}
                              <div>
                                <h5 className="text-md font-medium text-gray-800 mb-4">
                                  {t('kyc.step5.personalInformation')}
                                </h5>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step3.firstname')} *
                                    </label>
                                    <input
                                      type="text"
                                      name="firstname"
                                      value={editPoliticalForm.firstname}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                      placeholder={t('kyc.step3.firstnamePlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step3.lastname')} *
                                    </label>
                                    <input
                                      type="text"
                                      name="lastname"
                                      value={editPoliticalForm.lastname}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                      placeholder={t('kyc.step3.lastnamePlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step3.personalCode')}
                                    </label>
                                    <input
                                      type="text"
                                      name="personalCode"
                                      value={editPoliticalForm.personalCode}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                      placeholder={t('kyc.step3.personalCodePlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step3.citizenship')} *
                                    </label>
                                    <Select
                                      options={countryOptions}
                                      value={countryOptions.find(option => option.value === editPoliticalForm.citizenship)}
                                      onChange={(selectedOption) => setEditPoliticalForm(prev => ({ ...prev, citizenship: selectedOption?.value || '' }))}
                                      placeholder={t('kyc.step2.searchCountriesPlaceholder')}
                                      isSearchable
                                      menuPortalTarget={document.body}
                                      styles={selectStyles}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Political Details */}
                              <div>
                                <h5 className="text-md font-medium text-gray-800 mb-4">
                                  {t('kyc.step5.politicalDetails')}
                                </h5>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step5.institution')} *
                                    </label>
                                    <input
                                      type="text"
                                      name="institution"
                                      value={editPoliticalForm.institution}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                      placeholder={t('kyc.step5.institutionPlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step5.position')} *
                                    </label>
                                    <input
                                      type="text"
                                      name="position"
                                      value={editPoliticalForm.position}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                      placeholder={t('kyc.step5.positionPlaceholder')}
                                    />
                                  </div>
                                  <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('kyc.step5.relationship')} *
                                    </label>
                                    <select
                                      name="relationship"
                                      value={editPoliticalForm.relationship}
                                      onChange={handlePoliticalEditFormChange}
                                      className="w-full bg-[#F7F7FA] rounded-lg px-3 py-2 h-12 border border-transparent focus:outline-none focus:border-[#6B897B]"
                                    >
                                      <option value="">{t('kyc.step5.relationship')}</option>
                                      <option value="beneficial_owner">{t('kyc.step5.relationshipOption1')}</option>
                                      <option value="family_member">{t('kyc.step5.relationshipOption2')}</option>
                                      <option value="close_associate">{t('kyc.step5.relationshipOption3')}</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 flex gap-3">
                              <button
                                type="button"
                                onClick={handleSavePoliticalEdit}
                                className="px-4 py-2 bg-[#6B897B] text-white rounded-lg hover:bg-[#5A7A6A] transition"
                              >
                                {t('kyc.step5.save')}
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelPoliticalEdit}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                              >
                                {t('kyc.step5.cancel')}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-between">
                      <button type="button" className="px-8 py-3 rounded-full bg-gray-200 text-[#00564B] font-semibold hover:bg-[#C3ECD9] transition" onClick={handleBack}>‹ {t('kyc.step5.back')}</button>
                      <button type="button" className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition" onClick={handleNext}>{t('kyc.step5.next')} ›</button>
                    </div>
                  </div>

                  {/* Person Selection Modal */}
                  {showPersonSelectionModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-lg font-semibold text-gray-800">
                            {t('kyc.step5.selectPersons')}
                          </h3>
                          <button
                            type="button"
                            onClick={handleClosePersonSelectionModal}
                            className="text-gray-400 hover:text-gray-600 transition"
                          >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="space-y-4 mb-6">
                          {form.beneficialOwners
                            .filter(bo => !form.politicalMembers.some(pm => pm.personalCode === bo.personalCode))
                            .map((bo, index) => (
                              <div 
                                key={index} 
                                className={`border rounded-lg p-4 cursor-pointer transition ${
                                  selectedPersonForPolitical?.personalCode === bo.personalCode
                                    ? 'border-[#6B897B] bg-[#6B897B] bg-opacity-10'
                                    : 'border-gray-200 hover:border-[#6B897B] hover:bg-gray-50'
                                }`}
                                onClick={() => handleSelectPersonFromModal(bo)}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    selectedPersonForPolitical?.personalCode === bo.personalCode
                                      ? 'bg-[#6B897B]'
                                      : 'bg-gray-300'
                                  }`}>
                                    {selectedPersonForPolitical?.personalCode === bo.personalCode ? (
                                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                    )}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-[#00564B]">
                                      {bo.firstname} {bo.lastname}
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                      {t('kyc.step3.personalCode')}: {bo.personalCode}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                        
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={handleClosePersonSelectionModal}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                          >
                            {t('kyc.step5.cancel')}
                          </button>
                          <button
                            type="button"
                            onClick={handleConfirmSelectedPerson}
                            disabled={!selectedPersonForPolitical}
                            className={`px-4 py-2 rounded-lg transition ${
                              selectedPersonForPolitical
                                ? 'bg-[#6B897B] text-white hover:bg-[#5A7A6A]'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {t('kyc.step5.add')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 6: Review & Confirmation */}
              {step === 6 && (
                <div>
                  <h2 className="text-2xl font-semibold mb-8 text-[#00564B]">{t('kyc.step6.title')}</h2>
                  
                  <div className="space-y-8">
                    {/* Step 1: Company Data */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">1</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.steps.step1')}</h3>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.companyName')}:</span>
                          <span className="ml-2 font-medium">{form.companyName}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.registryCode')}:</span>
                          <span className="ml-2 font-medium">{form.registryCode}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.telephone')}:</span>
                          <span className="ml-2 font-medium">{form.telephone || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.email')}:</span>
                          <span className="ml-2 font-medium">{form.email || '-'}</span>
                        </div>
                        {/* Legal Address */}
                        <div className="col-span-2">
                          <span className="text-gray-500 font-semibold">{t('kyc.step1.legalAddress')}:</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.legalAddress')}:</span>
                          <span className="ml-2 font-medium">{form.legalAddress}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.legalDistrict')}:</span>
                          <span className="ml-2 font-medium">{form.legalCity}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.legalCounty')}:</span>
                          <span className="ml-2 font-medium">{form.legalCounty}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.legalCountry')}:</span>
                          <span className="ml-2 font-medium">{form.legalCountry}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step1.legalZipCode')}:</span>
                          <span className="ml-2 font-medium">{form.legalZipCode}</span>
                        </div>
                        
                        {/* Business Address */}
                        {form.differentAddress && (
                          <>
                            <div className="col-span-2">
                              <span className="text-gray-500 font-semibold">{t('kyc.step1.businessAddress')}:</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('kyc.step1.businessAddress')}:</span>
                              <span className="ml-2 font-medium">{form.businessAddress || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('kyc.step1.businessDistrict')}:</span>
                              <span className="ml-2 font-medium">{form.businessCity || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('kyc.step1.businessCounty')}:</span>
                              <span className="ml-2 font-medium">{form.businessCounty || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('kyc.step1.businessCountry')}:</span>
                              <span className="ml-2 font-medium">{form.businessCountry || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('kyc.step1.businessZipCode')}:</span>
                              <span className="ml-2 font-medium">{form.businessZipCode || '-'}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Step 2: Business Activity */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">2</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.steps.step2')}</h3>
                      </div>
                      
                      <div className="space-y-4 text-sm">
                        <div>
                          <span className="text-gray-500">{t('kyc.step2.activity')}:</span>
                          <span className="ml-2 font-medium">
                            {form.activityText || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step2.activityDescription')}:</span>
                          <span className="ml-2 font-medium">{form.activityDescription || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step2.activityAreas')}:</span>
                          <span className="ml-2 font-medium">
                            {form.activityAreas.length > 0 
                              ? form.activityAreas.map(code => 
                                  countries.find(c => c.alpha2 === code)?.name || code
                                ).join(', ')
                              : '-'
                            }
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">{t('kyc.step2.mainPartners')}:</span>
                          <span className="ml-2 font-medium">{form.mainPartners || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: Board Members */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">3</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.steps.step3')}</h3>
                      </div>
                      
                      <div className="space-y-4">
                        {form.boardMembers.length > 0 ? (
                          form.boardMembers.map((member, index) => (
                            <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                                <h4 className="font-medium text-[#00564B]">
                                  {member.firstname} {member.lastname}
                                </h4>
                              </div>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                  <span className="ml-2 font-medium">{member.personalCode}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                                  <span className="ml-2 font-medium">
                                    {countries.find(c => c.alpha2 === member.citizenship)?.name || member.citizenship}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                                  <span className="ml-2 font-medium">
                                    {member.birthday ? member.birthday.toLocaleDateString('et-EE', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    }) : '-'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{t('kyc.step3.experience')}:</span>
                                  <span className="ml-2 font-medium">
                                    {Math.floor(member.experience / 12)}a {member.experience % 12}k
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-center py-4">{t('kyc.step3.noMembers')}</p>
                        )}
                      </div>
                    </div>

                    {/* Step 4: Beneficial Owners */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">4</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.steps.step4')}</h3>
                      </div>
                      
                      {!form.beneficialOwnersObligatory ? (
                        <div className="space-y-4">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 text-yellow-800">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <span className="font-medium">{t('kyc.step6.noBeneficialOwners')}</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">{t('kyc.step4.reasonLabel')}:</span>
                            <span className="ml-2 font-medium">{form.reasonOfNoBO || '-'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {form.beneficialOwners.length > 0 ? (
                            form.beneficialOwners.map((bo, index) => (
                              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <h4 className="font-medium text-[#00564B]">
                                    {bo.firstname} {bo.lastname}
                                  </h4>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                    <span className="ml-2 font-medium">{bo.personalCode}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                                    <span className="ml-2 font-medium">
                                      {countries.find(c => c.alpha2 === bo.citizenship)?.name || bo.citizenship}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                                    <span className="ml-2 font-medium">
                                      {bo.birthday ? bo.birthday.toLocaleDateString('et-EE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      }) : '-'}
                                    </span>
                                  </div>

                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-4">{t('kyc.step4.noOwners')}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Step 5: Politically Exposed Person */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold text-sm">5</span>
                        </div>
                        <h3 className="text-lg font-semibold text-[#00564B]">{t('kyc.steps.step5')}</h3>
                      </div>
                      
                      {!form.hasPoliticalMembers ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 text-green-800">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="font-medium">{t('kyc.step6.noPoliticalMembers')}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {form.politicalMembers.length > 0 ? (
                            form.politicalMembers.map((pm, index) => (
                              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-8 h-8 bg-[#6B897B] rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                  </div>
                                  <h4 className="font-medium text-[#00564B]">
                                    {pm.firstname} {pm.lastname}
                                  </h4>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  {/* Personal Information */}
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.personalCode')}:</span>
                                    <span className="ml-2 font-medium">{pm.personalCode || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.citizenship')}:</span>
                                    <span className="ml-2 font-medium">
                                      {countries.find(c => c.alpha2 === pm.citizenship)?.name || pm.citizenship || '-'}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step3.birthday')}:</span>
                                    <span className="ml-2 font-medium">{pm.birthday ? pm.birthday.toLocaleDateString('et-EE') : '-'}</span>
                                  </div>
                                  
                                  {/* Political Details */}
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step5.institution')}:</span>
                                                                         <span className="ml-2 font-medium">{pm.institution || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step5.position')}:</span>
                                                                         <span className="ml-2 font-medium">{pm.position || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('kyc.step5.relationship')}:</span>
                                    <span className="ml-2 font-medium">
                                      {pm.relationship === 'beneficial_owner' ? t('kyc.step5.relationshipOption1') :
                                       pm.relationship === 'family_member' ? t('kyc.step5.relationshipOption2') :
                                       pm.relationship === 'close_associate' ? t('kyc.step5.relationshipOption3') : (pm.relationship || '-')}
                                    </span>
                                  </div>
                                  
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-center py-4">{t('kyc.step6.noPoliticalMembersAdded')}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-100 -mx-12 -mb-12 px-12 py-6 rounded-b-2xl mt-12">
                    <div className="flex justify-between">
                      <button type="button" className="px-8 py-3 rounded-full bg-gray-200 text-[#00564B] font-semibold hover:bg-[#C3ECD9] transition" onClick={handleBack}>‹ {t('kyc.step6.back')}</button>
                      <button 
                        type="button" 
                        className="px-8 py-3 rounded-full bg-[#00564B] text-white font-semibold hover:bg-[#003D34] transition disabled:bg-gray-400 disabled:cursor-not-allowed" 
                        onClick={handleNext}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Submitting...
                          </div>
                        ) : (
                          `${t('kyc.step6.submit')} ›`
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 7: Form Submission Result */}
              {step === 7 && (
                <div className="text-center py-12">
                  <div className="max-w-2xl mx-auto">
                    {submissionStatus === 'success' && (
                      <>
                        {/* Success Icon */}
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                          <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>

                        {/* Success Message */}
                        <h2 className="text-3xl font-bold text-[#00564B] mb-4">
                          {t('kyc.step7.thankYou')}
                        </h2>
                        
                        <p className="text-lg text-gray-600 mb-8">
                          {t('kyc.step7.successMessage')}
                        </p>    
                      
                      </>
                    )}

                    {submissionStatus === 'conflict' && (
                      <>
                        {/* Warning Icon */}
                        <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-8">
                          <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>

                        {/* Warning Message */}
                        <h2 className="text-3xl font-bold text-yellow-700 mb-4">
                          {t('kyc.step7.formAlreadySubmitted')}
                        </h2>
                        
                        <p className="text-lg text-gray-600 mb-8">
                          {t('kyc.step7.formAlreadySubmittedMessage')}
                        </p>

                        {/* Additional Info */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
                          <div className="flex items-center gap-3 mb-3">
                            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-yellow-900">
                              {t('kyc.step7.whatThisMeans')}
                            </h3>
                          </div>
                          <p className="text-yellow-800">
                            {t('kyc.step7.whatThisMeansDescription')}
                          </p>
                        </div>
                      </>
                    )}

                    {submissionStatus === 'error' && (
                      <>
                        {/* Error Icon */}
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
                          <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </div>

                        {/* Error Message */}
                        <h2 className="text-3xl font-bold text-red-700 mb-4">
                          {t('kyc.step7.submissionError')}
                        </h2>
                        
                        <p className="text-lg text-gray-600 mb-8">
                          {t('kyc.step7.submissionErrorMessage')}
                        </p>

                        {/* Additional Info */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
                          <div className="flex items-center gap-3 mb-3">
                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
                            </svg>
                            <h3 className="text-lg font-semibold text-red-900">
                              {t('kyc.step7.nextSteps')}
                            </h3>
                          </div>
                          <p className="text-red-800">
                            {t('kyc.step7.nextStepsDescription')}
                          </p>
                        </div>
                      </>
                    )}

                    {/* Back to Varahaldus Link - shown for all statuses */}
                    <div className="space-y-4">
                      <a
                        href={REDIRECT_URL}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-[#00564B] text-white rounded-lg hover:bg-[#003D34] transition font-semibold"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        {submissionStatus === 'success' ? t('kyc.step7.backToVarahaldus') : t('kyc.step7.backToMainPlatform')}
                      </a>
                      
                      <p className="text-sm text-gray-500">
                        {submissionStatus === 'success' ? t('kyc.step7.contactInfo') : t('kyc.step7.redirectMessage')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </main>
      </div>
        )}
    </div>
  );
}

export default KYCForm;
